import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { useInertiaCable } from "@inertia-cable/react"
import { toast } from "sonner"
import {
  ChevronRight,
  ChevronDown,
  Lock,
  Plug,
  Plus,
  Upload,
  AlertTriangle,
  Pause,
  Play,
  PowerOff,
  Sparkles,
  CheckCircle2,
  Circle,
  XCircle,
  RefreshCw,
  Download,
  Database,
  Globe,
  Mail,
  Copy,
  Check,
  Square,
  Trash2,
  Info,
  ArrowRight,
} from "lucide-react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { InfoTooltip } from "@/components/ui/info-tooltip"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Badge, type BadgeTone } from "@/components/ui/badge"
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog"
import { MessageMarkdown } from "@/components/domain/message-markdown"
import { fmtCompactRelative, fmtNumber, fmtRelativeTime } from "@/lib/format"
import { cn } from "@/lib/cn"
import { useCan } from "@/lib/authz"
import type { InertiaFormObject, SharedProps, CompanyLifecycleState } from "@/types/inertia"
import type {
  CredentialField,
  ConnectionFlow,
  ConnectionType,
  LifecycleState,
  ProviderKey,
  RunKind,
  RunState,
  PageState,
  JobPhase,
} from "@/types/support_connection"
import type {
  SupportImportJob,
  SupportImportProposedAgent,
  SupportImportProposedAgentBuildState,
} from "@/types/onboarding"

type SupportImportProposalKind =
  | "agent_header"
  | "skill"
  | "faq"
  | "escalation_reason"
  | "topic"
  | "agent_pool"
  | "ticket_queue"
  | "escalation_policy"
  | "pool_membership"
  | "team_invite"
type SupportImportProposalSummary = {
  id: number
  kind: SupportImportProposalKind
  status: string
  provenance?: string
  proposed_name?: string | null
  proposed_key?: string | null
  payload?: Record<string, unknown>
  edited_payload?: Record<string, unknown>
  duplicate_of_label?: string | null
  duplicate_resolution?: string | null
  compared_to_label?: string | null
  compared_to_score?: number | null
  operator_note?: string | null
}
type AgentBootstrapRunState = SupportImportProposedAgentBuildState
type AgentBootstrapRunSummary = {
  id: number
  state: AgentBootstrapRunState
  phase_summary: string | null
  created_at: string
  agent_id?: number | null
  agent_name?: string | null
}
type AgentBootstrapRunDetailType = AgentBootstrapRunSummary & {
  created_by_user_email: string
  applied_at: string | null
  compare_against_agent_id?: number | null
  compare_against_agent_name?: string | null
  proposed_agent_attrs?: Record<string, unknown>
  synthesis_warnings?: Record<string, unknown>
  proposals: SupportImportProposalSummary[]
}

// Settings → Onboarding. Four-tab guided flow that mirrors the
// operational lifecycle:
//
//   Tab 1: Initialize       →  pick provider, fill credentials, test+save
//   Tab 2: Monitor          →  watch ingest, manage runs, "fully migrated" cutover
//   Tab 3: Configure agents →  decide which connections feed which AI agent
//                              (visible once SupportImportJob reaches the
//                              configure_agents phase; gated below)
//   Tab 4: Review & apply   →  per-group bootstrap-review surface; renamed
//                              from the legacy "Generate" tab
//
// All four tabs render off the same controller payload; the React layer
// picks which tab to show via ?tab=initialize|monitor|configure|review.
// Default tab on first land: `monitor` if any connection exists, else
// `initialize`.
//
// Capability gating:
//   :support_connections_view — page render
//   :support_connections_edit — Tab 1 submit, Tab 2 pause/resume/disconnect,
//                               Tab 3 configure actions
//   :bootstrap_runs_apply     — Tab 4 apply

type ProviderEntry = {
  key:                      string
  display_name:             string
  connection_types:         ConnectionType[]
  oauth_config:             Record<string, unknown> | null
  rate_limit_per_min:       number | null
  // Legacy single-valued fields — populated from the FIRST connection type
  // by the controller for one merge cycle. Optional so new adapters that
  // only declare CONNECTION_TYPES still type-check.
  connection_flow?:         ConnectionFlow | null
  credential_schema?:       CredentialField[]
  onboarding_instructions?: string | null
}

type ConnectionRunPage = {
  id:            number
  seq:           number
  state:         PageState
  retry_count:   number
  error_message: string | null
  started_at:    string | null
  completed_at:  string | null
}

type ConnectionRun = {
  id:                 number
  kind:               RunKind
  state:              RunState
  started_at:         string | null
  completed_at:       string | null
  total_records:      number   // live-aware: prefers metadata.live_users_count when present, else durable
  failed_records:     number
  total_pages:        number
  live_users_count?:  number | null
  live_pages_count?:  number | null
  pages_fetched:      number
  pages_applied:      number
  pages_failed:       number
  estimated_records?: number
  elapsed_seconds?:   number | null
  eta_seconds?:       number | null
  error_message:      string | null
  pages:              ConnectionRunPage[]
  resource_statuses:  ResourceStatus[]
  // Phase state machine. Adapters with no phases emit an empty array
  // (legacy data path); the FE falls back to the conflated single-bar
  // progress display in that case.
  phase:               string | null
  phases:              JobPhase[]
  current_phase_label: string | null
  // Live-progress block written by SupportImports::ReconcileIngestErrors
  // every ~25 items. Drives the auto-recovery progress line below the
  // phase strip ("23/4338 done · N recovered · N marked system · N
  // unrecoverable"). Null until the reconciler fires for this run.
  reconciler:          ReconcilerProgress | null
  ingest_verification: IngestVerification | null
}

type IngestVerificationIssue = {
  severity:     "blocking" | "warning" | string
  kind:         string
  aspect_kind?: string | null
  message:      string
}

type IngestVerification = {
  ok:              boolean
  checked_at:      string | null
  totals:          Record<string, number>
  blocking_issues: IngestVerificationIssue[]
  warnings:        IngestVerificationIssue[]
}

type ReconcilerProgress = {
  phase:                 "starting" | "authors" | "tickets" | "complete" | "errored" | null
  processed:             number
  total:                 number
  recovered_tickets:     number
  recovered_authors:     number
  marked_system:         number
  unrecoverable_tickets: number
  unrecoverable_authors: number
  started_at:            string | null
  updated_at:            string | null
  finished_at:           string | null
  error:                 string | null
}

type PhaseErrorItem = {
  label:      string
  count:      number
  aspectKind: string
}

type PhaseErrorEntry = Record<string, unknown> & {
  ext_id?: string | number | null
  ticket_id?: string | number | null
  message_id?: string | number | null
  page_id?: string | number | null
  page_seq?: string | number | null
  subject?: string | null
  message_body?: string | null
  message_created_at?: string | null
  sample_message_body?: string | null
  sample_message_id?: string | number | null
  sample_message_created_at?: string | null
  zendesk_url?: string | null
  resolution?: string | null
  resolution_detail?: string | null
}

type PhaseErrorPayload = {
  count?: number
  sample_count?: number
  explanation?: string
  pagination?: {
    page: number
    per_page: number
    total_entries: number
    has_more: boolean
  }
  ticket_ids?: string[]
  entries?: PhaseErrorEntry[]
}

type ConnectionWebhook = {
  id:                  number
  kind:                "tickets" | "events"
  provider_webhook_id: string | null
  endpoint_url:        string | null
  state:               "active" | "errored" | "deleted_remote"
  last_received_at:    string | null
  last_error:          string | null
  subscriptions:       string[]
  trigger_count:       number
}

type ResourceState = "pending" | "running" | "succeeded" | "failed" | "skipped"

type ResourceStatus = {
  resource_kind:     string
  download_state:    ResourceState
  import_state:      ResourceState
  count_imported:    number
  count_failed:      number
  error_message:     string | null
  last_succeeded_at: string | null
}

type ExistingConnection = {
  id:                    number
  provider:              ProviderKey | string
  provider_display_name: string
  name:                  string | null
  // Adapter-natural identity for this connection (e.g. Zendesk subdomain).
  // Falls back to `name` when the adapter doesn't declare an IDENTITY_FIELD
  // or the field's value is blank. Rendered in monospace on the Initialize
  // tab's Active Connections list.
  identity:              string
  key:                   string
  lifecycle_state:       LifecycleState
  paused:                boolean
  provider_account_name: string | null
  provider_account_url:  string | null
  last_sync_at:          string | null
  last_test_status:      "ok" | "failed" | null
  last_test_error:       string | null
  last_tested_at:        string | null
  created_at:             string
  disconnected_at:        string | null
  raw_purge_due_at:       string | null
  ingest_rate_per_min:    number | null
  rate_limit_default:     number | null
  rate_limit_account_cap: number | null
  last_count_total?:      number | null
  last_count_total_at?:   string | null
  // Per-connection plus-addressed inbox for the email-forward bulk-import
  // flow. Always populated by the serializer (token is generated
  // before_create on every SupportPlatformConnection). Only surfaced in
  // the UI when `connection_type === "csv"`.
  import_email_address:   string
  // The connection type the operator picked at create time. "api" |
  // "csv". Persisted in settings.connection_type. Optional for legacy
  // rows that predate this column — those default to "api" downstream.
  connection_type?:       string | null
  // Zendesk-only: which Zendesk user attribute the operator picked at
  // connect time to map to `appuser.source_key`. Stored as either
  // "external_id" or "user_field:<custom_key>". Null for non-Zendesk
  // providers and for legacy rows that predate this setting.
  appuser_source_key_field?: string | null
  // Operator-curated allowlist of Zendesk custom-field `key`s this
  // connection should ingest as `CustomFieldDefinition` rows. Default
  // (empty array) means "ingest none". Surfaced inline on the Active
  // Connections card so operators can edit without leaving the page.
  ingest_field_keys?:     string[]
  // Whether the operator opted into webhook-driven sync at connect time.
  // Drives RegisterWebhook + the Monitor panel toggle.
  sync_events_to_valet?:  boolean
  // Set when the operator confirmed the cutover dialog. The Monitor
  // panel disables the sync toggle once this is set ("Onboarding
  // complete — sync ended").
  onboarding_completed_at?: string | null
  // The Zendesk-side webhooks we registered for this connection (one
  // per `kind`: tickets / events). Empty when sync_events_to_valet is
  // false or RegisterWebhook hasn't run yet.
  webhooks?:              ConnectionWebhook[]
  runs:                   ConnectionRun[]
  // Active SupportImportJob this connection is part of. Same row appears
  // on every connection in the run; the FE picks one and uses it. Null
  // once the company is fully migrated or has never started onboarding.
  support_import_job:         SupportImportJob | null
}

type BootstrapPrereq = { key: string; label: string; met: boolean }

type Props = {
  existing_connections:    ExistingConnection[]
  available_providers:     ProviderEntry[]
  bootstrap_runs:          AgentBootstrapRunSummary[]
  agents:                  Array<{ id: number; name: string }>
  bootstrap_prereqs:       BootstrapPrereq[]
  selected_run:            AgentBootstrapRunDetailType | null
  selected_run_capabilities: { apply: boolean; cancel: boolean; edit: boolean }
  page_capabilities: {
    can_view:             boolean
    can_edit:             boolean
    can_view_bootstrap:   boolean
    can_create_bootstrap: boolean
  }
  onboarding_source:  string | null
  onboarding_stream:  string
}

// Live snapshot of one SupportImportAspect, pushed via InertiaCable on
// every after_commit. Mirrors SupportImportAspect#progress_snapshot.
type AspectSnapshot = {
  aspect_id:            number
  job_id:               number
  connection_id:        number
  kind:                 string
  state:                string
  download_state:       string
  import_state:         string
  downloaded_count:     number
  ingested_count:       number
  total_records:        number
  total_pages:          number
  failed_records:       number
  next_cursor:          string | null
  cursor_date:          string | null
  last_page_fetched_at: string | null
  last_succeeded_at:    string | null
  error_message:        string | null
  destroyed?:           boolean
}

const LIFECYCLE_TONE: Record<LifecycleState, BadgeTone> = {
  active:       "success",
  connecting:   "warning",
  disconnected: "neutral",
  deleting:      "warning",
}

const RUN_STATE_TONE: Record<RunState, BadgeTone> = {
  pending:   "neutral",
  running:   "warning",
  completed: "success",
  partial:   "warning",
  failed:    "error",
  cancelled: "neutral",
}

const BOOTSTRAP_STATE_TONE: Record<AgentBootstrapRunState, BadgeTone> = {
  pending:           "neutral",
  clustering:        "warning",
  summarizing:       "warning",
  synthesizing:      "warning",
  ready_for_review:  "success",
  applied:           "success",
  failed:            "error",
  cancelled:         "neutral",
}

type TabKey = "initialize" | "monitor" | "configure" | "review"

function isTabKey(s: string | null | undefined): s is TabKey {
  return s === "initialize" || s === "monitor" || s === "configure" || s === "review"
}

// Pull the active SupportImportJob off the connection list. Server-side
// invariant: every connection in a single SupportImportJob carries the
// same `support_import_job` row (or null), so the first non-null wins.
function activeSupportImportJob(connections: ExistingConnection[]): SupportImportJob | null {
  for (const c of connections) {
    if (c.support_import_job) return c.support_import_job
  }
  return null
}

// Tab 3 (Configure) is only meaningful once the SupportImportJob has
// reached the `configure_agents` phase (or the operator already advanced
// past it — they can revisit). Show it whenever phase exists in any
// non-pending state, so operators see configuration context as soon as
// the system is ready for it.
function canShowConfigureTab(run: SupportImportJob | null): boolean {
  if (!run) return false
  return [
    "ingest_completed",
    "bootstrap_started",
    "bootstrap_completed",
    "completed",
  ].includes(run.phase)
}

export default function SettingsOnboarding(props: Props) {
  const pageObj = usePage<SharedProps>()
  const { props: page, url: pageUrl } = pageObj
  const flash = page.flash
  const company = page.current_company
  const lifecycleState: CompanyLifecycleState = company?.lifecycle_state ?? "onboarding"
  const can = useCan()
  const canEditLifecycle = can("support_connections_edit")
  const supportImportJob = activeSupportImportJob(props.existing_connections)
  const showConfigure = canShowConfigureTab(supportImportJob)
  const [tab, setTab] = useState<TabKey>(() => initialTab(props))

  // SupportImportAspect#broadcast_progress_snapshot pushes the row's
  // counters + cursor + state inline on every per-page write. We hold
  // a per-aspect-id map of the latest snapshot in local state; the
  // phase strip / count display reads from this map first, falling
  // back to props.existing_connections for the initial render.
  // No HTTP round-trip per page — the data IS the broadcast.
  const [, setAspectLive] = useState<Record<number, AspectSnapshot>>({})
  useInertiaCable(props.onboarding_stream, {
    only: [],
    onMessage: (data: Record<string, unknown>) => {
      const aspectId = data.aspect_id
      if (typeof aspectId !== "number") return
      setAspectLive((prev) => ({ ...prev, [aspectId]: data as AspectSnapshot }))
    },
  })

  // Sync `tab` state with the `?tab=` query param on every Inertia
  // navigation (not just first paint) so that controller redirects to
  // `?tab=monitor` after Test & Save actually switch the rendered tab.
  // Depends on `pageUrl` (Inertia's current URL) — that changes on every
  // visit, including same-component redirects. Legacy `?tab=generate`
  // links route to the renamed Review & apply tab.
  useEffect(() => {
    const u = new URL(pageUrl, window.location.origin)
    const raw = u.searchParams.get("tab")
    const fromUrl: TabKey | null = raw === "generate" ? "review" : isTabKey(raw) ? raw : null
    if (fromUrl && fromUrl !== tab) setTab(fromUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageUrl])

  const switchTab = (next: TabKey) => {
    setTab(next)
    const url = new URL(window.location.href)
    url.searchParams.set("tab", next)
    window.history.replaceState({}, "", url.toString())
  }

  return (
    <AppShell>
      <PageHeader
        eyebrow="Settings"
        title="Onboarding"
        subtitle="Connect a support platform, monitor the ingest, then optionally generate an AI agent from your historical data."
      />

      {lifecycleState === "active" && (
        <div className="rounded-control border border-status-success/30 bg-status-success/10 px-4 py-2 text-sm text-status-success">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              This company is marked active. The Onboarding tab is hidden from your sidebar but this page
              remains accessible.
            </div>
            {canEditLifecycle && <RestoreOnboardingButton />}
          </div>
        </div>
      )}

      {flash.notice && (
        <div className="rounded-control border border-status-success/30 bg-status-success/10 px-4 py-2 text-sm text-status-success">
          {flash.notice}
        </div>
      )}
      {flash.alert && (
        <div className="rounded-control border border-status-error/30 bg-status-error/10 px-4 py-2 text-sm text-status-error">
          {flash.alert}
        </div>
      )}

      <TabBar active={tab} onChange={switchTab} showConfigure={showConfigure} />

      {tab === "initialize" && <InitializeTab {...props} onConnected={() => switchTab("monitor")} />}
      {tab === "monitor" && (
        <MonitorTab
          connections={props.existing_connections}
          canEdit={props.page_capabilities.can_edit}
          supportImportJob={supportImportJob}
        />
      )}
      {tab === "configure" && (
        showConfigure && supportImportJob ? (
          <OnboardingConfigure
            run={supportImportJob}
            connections={props.existing_connections.map((c) => ({
              id:                    c.id,
              name:                  c.name,
              key:                   c.key,
              provider_display_name: c.provider_display_name,
              ticket_count:          c.runs.find((r) => r.kind === "download_tickets" && r.state === "completed")?.total_records,
            }))}
            agents={props.agents}
            canEdit={props.page_capabilities.can_edit}
          />
        ) : (
          <Card className="p-5 sm:p-6">
            <div className="flex flex-col items-start gap-2">
              <h3 className="text-sm font-semibold text-app-text">Configure isn't ready yet</h3>
              <p className="text-sm text-app-muted">
                The configure step unlocks once data ingestion completes. Watch progress on the
                Monitor tab.
              </p>
              <Button variant="ghost" size="sm" onClick={() => switchTab("monitor")}>
                Go to Monitor
              </Button>
            </div>
          </Card>
        )
      )}
      {tab === "review" && (
        <ReviewApplyTab
          run={supportImportJob}
          runs={props.bootstrap_runs}
          agents={props.agents}
          prereqs={props.bootstrap_prereqs}
          selectedRun={props.selected_run}
          selectedRunCapabilities={props.selected_run_capabilities}
          canView={props.page_capabilities.can_view_bootstrap}
        />
      )}
      {tab !== "review" && (company?.onboarding_at || company?.active_at) && (
        <div className="text-[11px] text-app-muted">
          {company?.onboarding_at && <>Onboarding started {fmtCompactRelative(company.onboarding_at)}.</>}
          {company?.active_at && <> Active since {fmtCompactRelative(company.active_at)}.</>}
        </div>
      )}
    </AppShell>
  )
}

function initialTab(props: Props): TabKey {
  if (typeof window !== "undefined") {
    const url = new URL(window.location.href)
    const raw = url.searchParams.get("tab")
    // `?tab=generate` from old links → renamed Review & apply tab.
    if (raw === "generate") return "review"
    if (isTabKey(raw)) return raw
  }
  return props.existing_connections.length > 0 ? "monitor" : "initialize"
}

// Debounced PATCH to /settings/onboarding/preferences. Used by Tab 1
// to persist the operator's provider pick without forcing a full form
// submit. The default-locale picker that used to share this hook moved
// to Settings → Company in Wave 21. The CSRF token is read on each
// flush so we don't go stale across long-lived sessions.
function useDebouncedPreferencePatch(delayMs = 350) {
  const pendingRef = useRef<Record<string, string>>({})
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const flush = useCallback(async () => {
    const body = pendingRef.current
    pendingRef.current = {}
    if (Object.keys(body).length === 0) return
    const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
    try {
      await fetch("/settings/onboarding/preferences", {
        method:      "PATCH",
        credentials: "same-origin",
        headers:     { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
        body:        JSON.stringify(body),
      })
    } catch {
      // Persistence is best-effort; the user can re-pick on next render.
    }
  }, [])

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current) }, [])

  return useCallback((patch: Record<string, string>) => {
    pendingRef.current = { ...pendingRef.current, ...patch }
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => { flush() }, delayMs)
  }, [delayMs, flush])
}

// ---------- Tab bar ----------

function TabBar({
  active,
  onChange,
  showConfigure,
}: {
  active: TabKey
  onChange: (t: TabKey) => void
  showConfigure: boolean
}) {
  const tabs: Array<{ key: TabKey; label: string; hint: string; disabled?: boolean }> = [
    { key: "initialize", label: "1. Initialize",       hint: "Connect a provider" },
    { key: "monitor",    label: "2. Monitor",          hint: "Watch the import" },
    {
      key:      "configure",
      label:    "3. Configure agents",
      hint:     showConfigure ? "Group connections" : "Unlocks after import",
      disabled: !showConfigure,
    },
    { key: "review",     label: "4. Review & apply",   hint: "Approve AI proposals" },
  ]
  return (
    <div className="thin-scrollbar -mx-4 flex overflow-x-auto border-b border-border px-4 sm:mx-0 sm:px-0">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          disabled={t.disabled}
          className={cn(
            "flex shrink-0 flex-col items-start gap-0.5 border-b-2 px-3 py-3 text-sm transition sm:px-4",
            active === t.key
              ? "border-brand-red text-app-text"
              : t.disabled
                ? "cursor-not-allowed border-transparent text-app-muted/50"
                : "border-transparent text-app-muted hover:text-app-text",
          )}
        >
          <span className="font-medium whitespace-nowrap">{t.label}</span>
          <span className="text-[11px] text-app-muted whitespace-nowrap">{t.hint}</span>
        </button>
      ))}
    </div>
  )
}

// ============================================================
// Tab 1 — Initialize
// ============================================================

function InitializeTab({
  existing_connections,
  available_providers,
  page_capabilities,
  onboarding_source,
  onConnected,
}: Props & { onConnected: () => void }) {
  // Initialize tab leads with a list of active connections (one card per row).
  // First-time UX is preserved: when there are zero connections, we auto-open
  // the new-connection panel and skip the "+ Connect another source" button.
  // After save, the controller redirects back to the same page so Inertia
  // refreshes `existing_connections` naturally — we just collapse the panel.
  const hasConnections = existing_connections.length > 0
  const [showNewConnectionPanel, setShowNewConnectionPanel] = useState<boolean>(!hasConnections)

  const handleConnected = () => {
    setShowNewConnectionPanel(false)
    onConnected()
  }

  return (
    <div className="space-y-6">
      {hasConnections && (
        <ActiveConnectionsList connections={existing_connections} />
      )}

      {hasConnections && !showNewConnectionPanel && page_capabilities.can_edit && (
        <Button
          variant="red"
          size="sm"
          type="button"
          onClick={() => setShowNewConnectionPanel(true)}
        >
          <Plus size={14} className="mr-1.5" aria-hidden /> Connect another source
        </Button>
      )}

      {showNewConnectionPanel && (
        <NewConnectionPanel
          available_providers={available_providers}
          page_capabilities={page_capabilities}
          onboarding_source={onboarding_source}
          onConnected={handleConnected}
          showCollapse={hasConnections}
          onCollapse={() => setShowNewConnectionPanel(false)}
        />
      )}
    </div>
  )
}

// ---------------- Active Connections List ----------------

// Renders one card per existing connection across ALL providers. Operators
// with two Zendesk subdomains (prod + sandbox) see two rows here, each
// keyed by its adapter-natural identity (e.g. subdomain).
function ActiveConnectionsList({ connections }: { connections: ExistingConnection[] }) {
  return (
    <Card className="p-5 sm:p-6">
      <header className="mb-3">
        <h2 className="text-lg font-semibold">Active connections</h2>
        <p className="text-sm text-app-muted">
          Each row is one connected source. You can connect multiple — for example a production
          and a sandbox subdomain.
        </p>
      </header>
      <ul className="space-y-2">
        {connections.map((c) => (
          <li key={c.id}>
            <ActiveConnectionRow connection={c} />
          </li>
        ))}
      </ul>
    </Card>
  )
}

function ActiveConnectionRow({ connection }: { connection: ExistingConnection }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-control border border-border bg-white/[0.02]">
      {/* Header — clickable to toggle expand. Whole row is the button so
          the operator can hit anywhere along it. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-4 rounded-control px-4 py-3 text-left transition-colors hover:bg-white/[0.03]"
        aria-expanded={open}
      >
        {/* Provider icon + display name + identity */}
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="flex h-9 w-9 flex-none items-center justify-center rounded-control bg-white/5 text-app-muted">
            <Globe size={16} aria-hidden />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium text-app-text">
              <span>{connection.provider_display_name}</span>
              <span className="font-mono text-xs text-app-muted">{connection.identity}</span>
            </div>
            <div className="mt-0.5 flex items-center gap-3 text-xs text-app-muted">
              <TestStatusChip connection={connection} />
              {connection.last_tested_at && (
                <span>last tested {fmtCompactRelative(connection.last_tested_at)}</span>
              )}
              {connection.appuser_source_key_field && (
                <SourceKeyChip
                  provider={connection.provider}
                  sourceKey={connection.appuser_source_key_field}
                />
              )}
            </div>
          </div>
        </div>

        {/* Forward-email chip — only for connections opted into the
            "API + Bulk export" path. API-only connections don't surface
            the inbox since they're not running JSON bulk imports. */}
        {connection.connection_type === "csv" && connection.import_email_address && (
          <ForwardEmailChip email={connection.import_email_address} />
        )}

        {/* Chevron toggle — rotates 90° when expanded. Replaces the
            previous "Manage" button; editing is now inline. */}
        <ChevronRight
          size={16}
          className={cn("flex-none text-app-muted transition-transform", open && "rotate-90")}
          aria-hidden
        />
      </button>

      {open && (
        <div className="border-t border-border px-4 py-4">
          <ActiveConnectionEditForm connection={connection} onClose={() => setOpen(false)} />
        </div>
      )}
    </div>
  )
}

// Inline edit form rendered when ActiveConnectionRow is expanded.
// PATCHes /settings/onboarding/connections/:id/ingest_field_keys with
// any subset of {name, appuser_source_key_field, ingest_field_keys}.
// The endpoint accepts partial updates so the operator can tweak a
// single field without resending the others.
function ActiveConnectionEditForm({
  connection,
  onClose,
}: {
  connection: ExistingConnection
  onClose: () => void
}) {
  const [name, setName] = useState<string>(connection.name ?? "")
  // `appuser_source_key_field` is locked after initial connect — no
  // editable state for it here; the form renders the persisted value
  // disabled.
  const [selected, setSelected] = useState<Set<string>>(
    new Set(Array.isArray(connection.ingest_field_keys) ? connection.ingest_field_keys : []),
  )
  const [saving, setSaving] = useState(false)

  // Probe state for both lists. Same endpoints as the Initialize-tab
  // form, but here we pass `connection_id` so the server uses the
  // persisted credentials.
  const [fieldsLoading, setFieldsLoading] = useState(true)
  const [fieldsError, setFieldsError] = useState<string | null>(null)
  const [fields, setFields] = useState<ZendeskFieldOption[]>([])

  const isZendesk = connection.provider === "zendesk"

  // Fetch the picker payload on mount.
  useEffect(() => {
    if (!isZendesk) {
      setFieldsLoading(false)
      return
    }
    let cancelled = false
    setFieldsLoading(true)
    setFieldsError(null)

    const fieldsP = fetch(
      `/settings/onboarding/zendesk_field_schema_options?connection_id=${connection.id}`,
      { headers: { Accept: "application/json" } },
    )
      .then(async (r) => ({ ok: r.ok, data: await r.json().catch(() => ({})) }))
      .then(({ ok, data }: { ok: boolean; data: { fields?: ZendeskFieldOption[]; ok?: boolean; error?: string | null } }) => {
        if (cancelled) return
        if (!ok || data.ok === false) {
          setFields([])
          setFieldsError(data.error || "could not load fields")
        } else {
          setFields(data.fields ?? [])
        }
      })
      .catch(() => { if (!cancelled) setFieldsError("network error — could not load custom fields") })

    fieldsP.finally(() => { if (!cancelled) setFieldsLoading(false) })

    return () => { cancelled = true }
  }, [isZendesk, connection.id])

  function toggle(key: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (checked) next.add(key); else next.delete(key)
      return next
    })
  }

  function save(e: FormEvent) {
    e.preventDefault()
    if (saving) return
    setSaving(true)

    const body: Record<string, unknown> = {
      ingest_field_keys: Array.from(selected),
    }
    if (name.trim() && name.trim() !== (connection.name ?? "")) body.name = name.trim()
    // appuser_source_key_field is locked after initial connect — every
    // imported row keys on it, so changing would orphan the dataset.
    // Field is disabled in the UI; we also don't send it in the body.

    const csrfMeta = typeof document !== "undefined" ? document.querySelector('meta[name="csrf-token"]') : null
    const csrf = csrfMeta?.getAttribute("content") || ""

    fetch(`/settings/onboarding/connections/${connection.id}/ingest_field_keys`, {
      method:  "PATCH",
      headers: {
        "Content-Type":   "application/json",
        Accept:           "application/json",
        "X-CSRF-Token":   csrf,
      },
      body: JSON.stringify(body),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}))
        if (!r.ok || data.ok === false) {
          throw new Error(data.error || `Save failed (${r.status})`)
        }
        toast.success("Connection updated")
        onClose()
        // Re-fetch the page so the serialized connection row reflects
        // the new ingest_field_keys / name / source-key value.
        router.reload({ only: [ "existing_connections" ] })
      })
      .catch((err: Error) => {
        toast.error(err.message ?? "Save failed")
      })
      .finally(() => setSaving(false))
  }

  return (
    <form onSubmit={save} className="space-y-4">
      <FormField label="Connection name" hint="Shown on the Active connections list. Format: Brand + Environment.">
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </FormField>

      {isZendesk && (
        <FormField
          label="Map appuser to"
          hint="Zendesk user attribute used as your customer's identity in Valet. Locked once set — every imported ticket and end-user keys on this field, so changing it would orphan all the existing rows. Reach out to support if you need a re-mapping."
        >
          <Input
            value={connection.appuser_source_key_field ?? "external_id"}
            disabled
            readOnly
            className="cursor-not-allowed opacity-60"
          />
          <p className="mt-1 text-xs text-app-muted">
            Current: <code className="font-mono">{fmtSourceKeyField(connection.appuser_source_key_field ?? "external_id")}</code>
          </p>
        </FormField>
      )}

      {isZendesk && (connection.webhooks?.length ?? 0) > 0 && (
        <FormField
          label="Webhooks"
          hint="Provider-side webhooks Valet registered for this connection. Manage them from the Monitor tab."
        >
          <ActiveConnectionWebhookList webhooks={connection.webhooks ?? []} />
        </FormField>
      )}

      {isZendesk && (
        <FormField
          label="Custom fields to ingest"
          hint="Select which Zendesk custom fields to import as Valet custom-field definitions. Off by default."
        >
          <CustomFieldsIngestPicker
            fields={fields}
            selected={selected}
            onToggle={toggle}
            onSelectAll={() => setSelected(new Set(fields.map((f) => f.key)))}
            onDeselectAll={() => setSelected(new Set())}
            loading={fieldsLoading}
            error={fieldsError}
          />
        </FormField>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  )
}



// Read-only summary of the webhooks Valet registered for this connection.
// Rendered inside the expanded Active Connection card so operators can
// see "is sync wired up?" without leaving the Initialize tab. Mutations
// (toggle off, re-register, send test) live on the Monitor tab — this
// is informational only.
function ActiveConnectionWebhookList({ webhooks }: { webhooks: ConnectionWebhook[] }) {
  return (
    <ul className="space-y-1.5">
      {webhooks.map((w) => {
        const tone = w.state === "active" ? "success" : (w.state === "errored" ? "danger" : "warning")
        const last = w.last_received_at ? fmtRelativeTime(w.last_received_at) : "never"
        const label = w.kind === "tickets" ? "Tickets + comments" : "End-users + KB articles"
        return (
          <li
            key={w.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-control border border-border bg-white/[0.02] px-3 py-2 text-sm"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-app-text">
                <span>{label}</span>
                <Badge tone={tone} className="px-2 py-0">{w.state}</Badge>
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[12px] text-app-muted">
                <span>last event: {last}</span>
                {w.kind === "tickets" && (
                  <span>· {w.trigger_count} trigger{w.trigger_count === 1 ? "" : "s"}</span>
                )}
                {w.last_error && <span className="text-status-error">· {w.last_error}</span>}
              </div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

// Source-key chip — render the operator's appuser-source-key-field
// choice in the chip format `source_key ⇒ ZenDesk.CustomField[<value>]`.
// Wraps gracefully on narrow viewports (chip flexes; tooltip stays
// inline). The `(?)` pulls open a hover/focus tooltip explaining what
// `source_key` is for non-technical operators.
function SourceKeyChip({ provider, sourceKey }: { provider: string; sourceKey: string }) {
  const { kind, value } = decomposeSourceKey(sourceKey)
  const providerName    = providerChipName(provider)

  return (
    <span className="inline-flex flex-wrap items-center gap-1 leading-relaxed">
      <span className="font-mono text-app-muted">source_key</span>
      <span aria-hidden>⇒</span>
      <span className="font-mono text-app-text">
        {providerName}.{kind}[{value}]
      </span>
      <InfoTooltip side="top">
        <span>
          This is YOUR unique identifier for each end user — the value you&rsquo;ll send to
          Valet from your API and other systems.
        </span>
        <span className="mt-2 block">
          When a customer support ticket comes in from {providerName}, Valet uses the value of
          this field on the {providerName} user to look up the matching <code className="font-mono">source_key</code>{" "}
          on your appusers, so we can attribute the conversation to the right person on your side.
        </span>
      </InfoTooltip>
    </span>
  )
}

// Probed Zendesk custom-field row, returned by
// /settings/onboarding/zendesk_field_schema_options.
type ZendeskFieldOption = {
  external_id: string
  key:         string
  title:       string
  type:        string
  scope:       string
  description: string | null
}

// Reusable picker component for the "Custom fields to ingest" section.
// Default behavior: every probed field is UNCHECKED. Operator opts in
// per field. Used in two places:
//   - Initialize tab credential form (pre-create, in-memory creds)
//   - Active Connections inline-expand editor (post-create, by id)
function CustomFieldsIngestPicker({
  fields,
  selected,
  onToggle,
  onSelectAll,
  onDeselectAll,
  loading,
  error,
}: {
  fields:        ZendeskFieldOption[]
  selected:      Set<string>
  onToggle:      (key: string, checked: boolean) => void
  onSelectAll:   () => void
  onDeselectAll: () => void
  loading:       boolean
  error:         string | null
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-control border border-border bg-white/[0.02] px-3 py-2 text-sm text-app-muted">
        <RefreshCw size={14} className="animate-spin text-app-muted" aria-hidden />
        Loading custom fields from Zendesk…
      </div>
    )
  }
  if (error) {
    return (
      <div className="rounded-control border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">
        Could not load custom fields: {error}
      </div>
    )
  }
  if (!fields.length) {
    return (
      <div className="rounded-control border border-border bg-white/[0.02] px-3 py-2 text-sm text-app-muted">
        No custom fields found in Zendesk. Nothing to import.
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="text-app-muted">
          {selected.size} of {fields.length} selected
        </span>
        <div className="flex items-center gap-3">
          <button type="button" className="text-app-link hover:underline" onClick={onSelectAll}>Select all</button>
          <span className="text-app-muted">·</span>
          <button type="button" className="text-app-link hover:underline" onClick={onDeselectAll}>Deselect all</button>
        </div>
      </div>
      <ul className="grid max-h-72 grid-cols-1 gap-px overflow-y-auto rounded-control border border-border bg-white/[0.02] p-1 sm:grid-cols-2">
        {fields.map((f) => {
          const checked = selected.has(f.key)
          return (
            <li key={`${f.scope}:${f.key}`}>
              <label className="flex min-w-0 cursor-pointer items-center gap-2 rounded-control px-2 py-1 text-sm hover:bg-white/[0.04]">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => onToggle(f.key, e.target.checked)}
                  className="flex-none"
                />
                <span className="min-w-0 flex-1 truncate text-app-text" title={f.title}>{f.title}</span>
                <code className="hidden flex-none rounded bg-white/[0.06] px-1 font-mono text-[11px] text-app-muted lg:inline">{f.key}</code>
                <Badge tone="neutral" className="flex-none px-1.5 py-0 text-[10px]">{f.type}</Badge>
                <Badge tone={f.scope === "ticket" ? "info" : "neutral"} className="flex-none px-1.5 py-0 text-[10px]">{f.scope}</Badge>
              </label>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function ForwardEmailChip({ email }: { email: string }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(email)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // clipboard unavailable — silent
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      className="flex items-center gap-1.5 rounded-control border border-border bg-white/[0.04] px-2 py-1 text-xs text-app-text hover:bg-white/[0.08]"
      title="Copy forwarding email"
    >
      <Mail size={12} className="text-app-muted" aria-hidden />
      <code className="font-mono">{email}</code>
      {copied ? (
        <Check size={12} className="text-status-success" aria-hidden />
      ) : (
        <Copy size={12} className="text-app-muted" aria-hidden />
      )}
    </button>
  )
}

// ---------------- New Connection Panel ----------------

// Wraps the legacy "pick provider → setup instructions + import options" flow.
// Same behavior as before — just rehomed under a collapsible card so the
// Active Connections list can lead the page.
function NewConnectionPanel({
  available_providers,
  page_capabilities,
  onboarding_source,
  onConnected,
  showCollapse,
  onCollapse,
}: {
  available_providers: ProviderEntry[]
  page_capabilities: Props["page_capabilities"]
  onboarding_source: string | null
  onConnected: () => void
  showCollapse: boolean
  onCollapse: () => void
}) {
  const [providerKey, setProviderKey] = useState<string>(onboarding_source ?? "")
  const provider = useMemo(
    () => available_providers.find((p) => p.key === providerKey) ?? null,
    [available_providers, providerKey],
  )

  // Connection-type sub-pick. Defaults to the first declared type in the
  // adapter's CONNECTION_TYPES (Zendesk: "api"). Resets when the operator
  // switches providers.
  const [connectionTypeKey, setConnectionTypeKey] = useState<string>("")
  useEffect(() => {
    if (provider && provider.connection_types && provider.connection_types.length > 0) {
      const first = provider.connection_types[0]?.type ?? ""
      setConnectionTypeKey(first)
    } else {
      setConnectionTypeKey("")
    }
  }, [providerKey, provider])

  const connectionType = useMemo<ConnectionType | null>(() => {
    if (!provider || !connectionTypeKey) return null
    return provider.connection_types?.find((t) => t.type === connectionTypeKey) ?? null
  }, [provider, connectionTypeKey])

  // Debounced PATCH to /settings/onboarding/preferences. We only
  // want to persist the operator's choice — no full form submit. The
  // 350ms window swallows back-to-back changes when they're scrubbing
  // through the dropdown via keyboard.
  const persistPreference = useDebouncedPreferencePatch()

  const onPickProvider = (next: string) => {
    setProviderKey(next)
    if (next) persistPreference({ onboarding_source: next })
  }

  // The NEW connection panel never reuses an existing connection — the
  // operator is creating a fresh row. Passing an existing match here
  // would falsely flip CsvPlusApiBranch into Stage 2 ("API connected ✓")
  // and leak the prior connection's ticket count into the banner.
  // Existing connections are managed from the Active Connections list
  // above; this panel always starts blank.
  const existingForProvider: ExistingConnection | null = null

  return (
    <Card className="p-5 sm:p-6">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Connect a new platform</h2>
          <p className="text-sm text-app-muted">
            Pick a provider and connection type to see step-by-step setup instructions and the credential form.
          </p>
        </div>
        {showCollapse && (
          <Button variant="ghost" size="sm" type="button" onClick={onCollapse}>
            Cancel
          </Button>
        )}
      </header>

      <div className="grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <div className="mb-1 text-xs uppercase tracking-wide text-app-muted">Provider</div>
          <Select<string>
            value={providerKey ? { value: providerKey, label: provider?.display_name ?? providerKey } : null}
            options={available_providers.map((p) => ({ value: p.key, label: p.display_name }))}
            onChange={(o) => onPickProvider(o?.value ?? "")}
            placeholder="Choose a provider…"
          />
          {!providerKey && (
            <div className="mt-1 text-[11px] text-app-muted">No default; please pick one.</div>
          )}
        </div>

        {provider && (provider.connection_types?.length ?? 0) > 0 && (
          <div>
            <div className="mb-1 text-xs uppercase tracking-wide text-app-muted">Connection type</div>
            <Select<string>
              value={
                connectionTypeKey
                  ? {
                      value: connectionTypeKey,
                      label: connectionType?.label ?? connectionTypeKey,
                    }
                  : null
              }
              options={provider.connection_types.map((t) => ({ value: t.type, label: t.label }))}
              onChange={(o) => setConnectionTypeKey(o?.value ?? "")}
              placeholder="Choose a connection type…"
            />
          </div>
        )}
      </div>

      {provider && connectionType && (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div>
            <div className="mb-2 text-xs uppercase tracking-wide text-app-muted">Setup instructions</div>
            <div className="rounded-control border border-border bg-white/[0.02] p-4">
              {connectionType.instructions ? (
                <MessageMarkdown content={connectionType.instructions} />
              ) : (
                <p className="text-sm text-app-muted">
                  No setup instructions available for {provider.display_name} ({connectionType.label}) yet —
                  fill in the credential form to the right.
                </p>
              )}
            </div>
          </div>

          <div>
            {page_capabilities.can_edit ? (
              <>
                <ConnectionFlowPanel
                  provider={provider}
                  connectionType={connectionType}
                  lastConnection={existingForProvider}
                  onConnected={onConnected}
                />
                <CountTotalBanner
                  connection={existingForProvider}
                  provider={provider}
                  onSwitchToCsv={() => setConnectionTypeKey("csv")}
                />
              </>
            ) : (
              <Card className="p-4">
                <p className="text-sm text-app-muted">
                  You don't have permission to add a connection. Ask an admin to finish onboarding.
                </p>
              </Card>
            )}
          </div>
        </div>
      )}
    </Card>
  )
}

// Count + recommendation banner. Renders nothing when we don't have a
// total to show. The "Found N tickets…" info card always renders when
// N > 0; the warning card renders only when N > 100k (≈ > 1 day import)
// and offers a CTA that flips the parent's Connection-type dropdown to
// `csv` (the internal slug — operator copy says "JSON bulk export").
function CountTotalBanner({
  connection,
  provider,
  onSwitchToCsv,
}: {
  connection: ExistingConnection | null
  provider: ProviderEntry
  onSwitchToCsv: () => void
}) {
  const total = connection?.last_count_total ?? null
  if (!total || total <= 0) return null

  const overThreshold = total > 100_000
  const days = Math.ceil(total / 100_000)
  const hasBulkExportType = provider.connection_types?.some((t) => t.type === "csv") ?? false

  return (
    <div className="mt-3 space-y-2">
      <div className="rounded-control border border-border bg-white/[0.03] px-3 py-2 text-sm text-app-text">
        Found {fmtNumber(total)} tickets in your {provider.display_name}.
      </div>
      {overThreshold && (
        <div className="flex items-start gap-2 rounded-control border border-status-warning/40 bg-status-warning/10 px-3 py-2 text-sm text-app-text">
          <AlertTriangle size={16} className="mt-0.5 flex-none text-status-warning" aria-hidden />
          <div className="flex-1">
            <p>
              Found {fmtNumber(total)} tickets. Importing through the API will take roughly{" "}
              {days} {days === 1 ? "day" : "days"} at ~100,000 tickets per 24 hours due to
              Zendesk's rate limits. Switch to the JSON bulk export connection type for a
              quicker initial backfill — the API will resume incremental syncing automatically
              once the bulk ingest finishes.
            </p>
            {hasBulkExportType && (
              <div className="mt-2">
                <Button type="button" size="sm" onClick={onSwitchToCsv}>
                  Switch to JSON bulk export
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function ConnectionFlowPanel({
  provider,
  connectionType,
  lastConnection,
  onConnected,
}: {
  provider: ProviderEntry
  connectionType: ConnectionType
  lastConnection: ExistingConnection | null
  onConnected: () => void
}) {
  switch (connectionType.flow) {
    case "credential_form":
      return (
        <CredentialFormBranch
          schema={connectionType.schema}
          providerKey={provider.key}
          connectionTypeKey={connectionType.type}
          lastConnection={lastConnection}
        />
      )
    case "oauth_redirect":
      return <OauthRedirectBranch providerKey={provider.key} providerName={provider.display_name} />
    case "bulk_upload":
      return <BulkUploadBranch />
    case "csv_plus_api":
      return (
        <CsvPlusApiBranch
          schema={connectionType.schema}
          providerKey={provider.key}
          lastConnection={lastConnection}
          onConnected={onConnected}
        />
      )
    default:
      return (
        <Card className="p-4">
          <p className="text-sm text-app-muted">Unknown connection flow for {provider.display_name}.</p>
        </Card>
      )
  }
}

function CredentialFormBranch({
  schema,
  providerKey,
  connectionTypeKey,
  lastConnection,
}: {
  schema: CredentialField[]
  providerKey: string
  // The connection type the operator picked. Persisted server-side so
  // the Active Connections list can show / hide the forward-email chip
  // (only "csv" connections are doing bulk imports).
  connectionTypeKey: string
  lastConnection?: ExistingConnection | null
}) {
  const [name, setName] = useState("")
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(schema.map((f) => [f.key, ""])),
  )
  const [submitting, setSubmitting] = useState(false)
  // Default ON. Operator unchecks if they want polling-only without
  // any provider-side webhooks registered. Persists to
  // `support_platform_connections.sync_events_to_valet`. Driving signal
  // for `RegisterWebhook` at connection lifecycle activation.
  const [syncEventsToValet, setSyncEventsToValet] = useState(true)

  // How much ticket history to import. Default: all time. Anything
  // narrower seeds the incremental cursor at Unix(now - N years) so
  // the API fetch starts there. Comments + audits cascade naturally
  // (we only fetch them for tickets we ingested). Identity / KB /
  // config streams are full-snapshot — the cutoff doesn't apply.
  const [historyWindowKey, setHistoryWindowKey] = useState<string>("all")
  // Whether to include soft-deleted tickets in the import (and the
  // count probe). Default true — matches current ingest behavior.
  // Operators uncheck if they want Valet to respect their explicit
  // ticket deletions (e.g. GDPR purges) and skip those rows.
  const [includeDeletedTickets, setIncludeDeletedTickets] = useState<boolean>(true)
  // Probed ticket count for the chosen window. null until the window
  // probe completes; "all" reads from the baseline `probeTicketCount`.
  const [windowedTicketCount, setWindowedTicketCount] = useState<number | null>(null)
  const [windowedProbing, setWindowedProbing]         = useState<boolean>(false)
  // Per-status breakdown for the windowed count — same shape as the
  // baseline (active / archived / deleted). Drives the breakdown
  // pills under every dropdown choice, not just All time.
  const [windowedBreakdown, setWindowedBreakdown] = useState<{
    total: number; active: number; archived: number; deleted: number
  } | null>(null)
  const [userIdOptions, setUserIdOptions] = useState<
    Array<{ key: string; label: string; recommended?: boolean }>
  >([])
  const [appuserSourceKeyField, setAppuserSourceKeyField] = useState<string>("")

  // Custom-fields opt-in allowlist. Default: nothing checked. Operator
  // explicitly picks the fields they want imported as
  // `CustomFieldDefinition` rows; the rest are skipped at ingest time.
  const [fieldSchemaOptions, setFieldSchemaOptions] = useState<ZendeskFieldOption[]>([])
  const [selectedFieldKeys, setSelectedFieldKeys] = useState<Set<string>>(new Set())
  const [fieldsLoading, setFieldsLoading] = useState(false)
  const [fieldsError, setFieldsError] = useState<string | null>(null)

  // Live probe of the credentials. Fires on every change of subdomain/email/api_token
  // (debounced via the existing useEffect cleanup). Drives:
  //   - inline "Cannot connect" red banner + disabled Test & save
  //   - "Found N tickets" neutral banner
  //   - >100k orange API+Bulk warning
  const [probeStatus, setProbeStatus] = useState<"idle" | "probing" | "ok" | "failed">("idle")
  const [probeTicketCount, setProbeTicketCount] = useState<number | null>(null)
  const [probeActiveCount, setProbeActiveCount] = useState<number | null>(null)
  const [probeDeletedCount, setProbeDeletedCount] = useState<number | null>(null)
  const [probeError, setProbeError] = useState<string | null>(null)

  // Filter: hidden fields never show. The Connection-type dropdown on the
  // Initialize tab now picks the right schema slice, so there's no
  // `auth_kind`-driven `tab_controlled` / `when:` plumbing here anymore.
  // We still honor `when:` for any future conditional field whose key is
  // present in the schema (e.g. mutually-exclusive optional fields).
  const visibleSchema = useMemo(
    () =>
      schema.filter((f) => {
        const ext = f as CredentialField & { when?: Record<string, string | null> }
        if (ext.hidden) return false
        if (ext.when) {
          for (const [whenKey, whenVal] of Object.entries(ext.when)) {
            if (values[whenKey]?.toString() !== whenVal?.toString()) return false
          }
        }
        return true
      }),
    [schema, values],
  )

  const canSubmit = useMemo(() => {
    if (name.trim().length === 0) return false
    return visibleSchema.every((f) => !f.required || (values[f.key] ?? "").trim().length > 0)
  }, [name, visibleSchema, values])

  // For Zendesk: once subdomain/email/api_token are all filled, hit the
  // in-memory options endpoint and surface the picker. On any failure we
  // hide the dropdown — operator can save with the default and edit later.
  // Both the `:api` and `:csv` connection types include those three fields.
  const isZendeskApiToken = providerKey === "zendesk"
  const zSubdomain = values.subdomain ?? ""
  const zEmail     = values.email ?? ""
  const zApiToken  = values.api_token ?? ""
  useEffect(() => {
    if (!isZendeskApiToken) {
      setUserIdOptions([])
      setProbeStatus("idle")
      setProbeTicketCount(null)
      setProbeActiveCount(null)
      setProbeDeletedCount(null)
      setProbeError(null)
      return
    }
    if (!zSubdomain.trim() || !zEmail.trim() || !zApiToken.trim()) {
      setUserIdOptions([])
      setProbeStatus("idle")
      setProbeTicketCount(null)
      setProbeActiveCount(null)
      setProbeDeletedCount(null)
      setProbeError(null)
      return
    }
    let cancelled = false
    // 1-second debounce — typing subdomain / agent email shouldn't hammer
    // Zendesk on every keystroke. Probe fires once after the operator
    // stops typing for a beat.
    const t = setTimeout(() => {
      setProbeStatus("probing")
      const params = new URLSearchParams({
        subdomain: zSubdomain.trim(),
        email:     zEmail.trim(),
        api_token: zApiToken.trim(),
      })
      fetch(`/settings/onboarding/zendesk_user_id_options?${params.toString()}`, {
        headers: { Accept: "application/json" },
      })
        .then(async (r) => {
          const json = await r.json().catch(() => ({}))
          return { ok: r.ok, data: json }
        })
        .then(({ ok, data }: {
          ok: boolean
          data: {
            options?: Array<{ key: string; label: string; recommended?: boolean }>
            ok?: boolean
            error?: string | null
            ticket_count?: number | null
            active_ticket_count?: number | null
            deleted_ticket_count?: number | null
          }
        }) => {
          if (cancelled) return
          const opts = data.options ?? []
          setUserIdOptions(opts)
          // Do NOT auto-pick a default. Per ops feedback, the operator
          // should actively choose this value — auto-defaulting to
          // external_id silently commits them to a one-way decision they
          // may not have considered. Save button is gated below until
          // they pick something.
          const probedOk = ok && (data.ok !== false)
          setProbeStatus(probedOk ? "ok" : "failed")
          setProbeTicketCount(typeof data.ticket_count === "number" ? data.ticket_count : null)
          setProbeActiveCount(typeof data.active_ticket_count === "number" ? data.active_ticket_count : null)
          setProbeDeletedCount(typeof data.deleted_ticket_count === "number" ? data.deleted_ticket_count : null)
          setProbeError(probedOk ? null : (data.error || null))
        })
        .catch(() => {
          if (cancelled) return
          setUserIdOptions([])
          setProbeStatus("failed")
          setProbeTicketCount(null)
          setProbeActiveCount(null)
          setProbeDeletedCount(null)
          setProbeError("network error — could not reach the probe endpoint")
        })
    }, 1000)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [isZendeskApiToken, zSubdomain, zEmail, zApiToken])

  // History-window dropdown options + helpers. `days: null` means
  // unbounded ("All time"); we send no `since=` param in that case.
  // Counts include soft-deleted tickets so the number matches the
  // operator's mental model of "what's in this Zendesk."
  const HISTORY_WINDOWS = useMemo(() => ([
    { key: "all", label: "All time",  days: null  as number | null },
    { key: "5y",  label: "5 years",   days: 1825 },
    { key: "3y",  label: "3 years",   days: 1095 },
    { key: "2y",  label: "2 years",   days: 730 },
    { key: "1y",  label: "1 year",    days: 365 },
    { key: "6mo", label: "6 months",  days: 180 },
    { key: "3mo", label: "3 months",  days: 90 },
  ]), [])

  function historyCutoffUnix(key: string): number | null {
    const w = HISTORY_WINDOWS.find((x) => x.key === key)
    if (!w || w.days == null) return null
    return Math.floor((Date.now() - w.days * 86400_000) / 1000)
  }

  // Probe the ticket count for a non-default window. Fires only when
  // the operator picks a window other than "All time" AND the main
  // credentials probe has reached :ok (so we know the credentials are
  // good). Hits the lightweight count endpoint to avoid re-running
  // user_id_options on every dropdown change.
  useEffect(() => {
    if (!isZendeskApiToken || probeStatus !== "ok") {
      setWindowedTicketCount(null)
      setWindowedBreakdown(null)
      setWindowedProbing(false)
      return
    }
    if (historyWindowKey === "all") {
      setWindowedTicketCount(null)
      setWindowedBreakdown(null)
      setWindowedProbing(false)
      return
    }
    const since = historyCutoffUnix(historyWindowKey)
    if (since == null) return

    let cancelled = false
    setWindowedProbing(true)
    const params = new URLSearchParams({
      subdomain:        zSubdomain.trim(),
      email:            zEmail.trim(),
      api_token:        zApiToken.trim(),
      since:            String(since),
      include_deleted:  includeDeletedTickets ? "1" : "0",
    })
    fetch(`/settings/onboarding/zendesk_ticket_count?${params.toString()}`, {
      headers: { Accept: "application/json" },
    })
      .then(async (r) => {
        const json = await r.json().catch(() => ({}))
        return { ok: r.ok, data: json }
      })
      .then(({ ok, data }: {
        ok: boolean
        data: {
          count?: number | null
          ok?: boolean
          breakdown?: { total: number; active: number; archived: number; deleted: number } | null
        }
      }) => {
        if (cancelled) return
        if (ok && data.ok !== false && typeof data.count === "number") {
          setWindowedTicketCount(data.count)
          setWindowedBreakdown(data.breakdown ?? null)
        } else {
          setWindowedTicketCount(null)
          setWindowedBreakdown(null)
        }
      })
      .catch(() => {
        if (cancelled) {
          return
        }
        setWindowedTicketCount(null)
        setWindowedBreakdown(null)
      })
      .finally(() => {
        if (!cancelled) setWindowedProbing(false)
      })
    return () => { cancelled = true }
  }, [isZendeskApiToken, probeStatus, historyWindowKey, includeDeletedTickets, zSubdomain, zEmail, zApiToken])

  // Once the credentials probe goes "ok", probe the field-schema
  // endpoint to populate the opt-in checkbox grid. Independent of the
  // user-id-options probe so failures don't cross-contaminate.
  useEffect(() => {
    if (!isZendeskApiToken || probeStatus !== "ok") {
      setFieldSchemaOptions([])
      setFieldsError(null)
      return
    }
    if (!zSubdomain.trim() || !zEmail.trim() || !zApiToken.trim()) return

    let cancelled = false
    setFieldsLoading(true)
    setFieldsError(null)
    const params = new URLSearchParams({
      subdomain: zSubdomain.trim(),
      email:     zEmail.trim(),
      api_token: zApiToken.trim(),
    })
    fetch(`/settings/onboarding/zendesk_field_schema_options?${params.toString()}`, {
      headers: { Accept: "application/json" },
    })
      .then(async (r) => {
        const json = await r.json().catch(() => ({}))
        return { ok: r.ok, data: json }
      })
      .then(({ ok, data }: {
        ok: boolean
        data: { fields?: ZendeskFieldOption[]; ok?: boolean; error?: string | null }
      }) => {
        if (cancelled) return
        if (!ok || data.ok === false) {
          setFieldSchemaOptions([])
          setFieldsError(data.error || "could not load fields")
        } else {
          setFieldSchemaOptions(data.fields ?? [])
        }
      })
      .catch(() => {
        if (cancelled) return
        setFieldSchemaOptions([])
        setFieldsError("network error — could not load custom fields")
      })
      .finally(() => {
        if (!cancelled) setFieldsLoading(false)
      })
    return () => { cancelled = true }
  }, [isZendeskApiToken, probeStatus, zSubdomain, zEmail, zApiToken])

  function toggleFieldKey(key: string, checked: boolean) {
    setSelectedFieldKeys((prev) => {
      const next = new Set(prev)
      if (checked) next.add(key); else next.delete(key)
      return next
    })
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit || submitting) return
    setSubmitting(true)
    const config: Record<string, string> = { ...values }
    if (appuserSourceKeyField) config.appuser_source_key_field = appuserSourceKeyField
    const settings: InertiaFormObject = {
      ingest_field_keys: Array.from(selectedFieldKeys),
    }
    const cutoffUnix = historyCutoffUnix(historyWindowKey)
    if (cutoffUnix != null) {
      config.history_cutoff_unix = String(cutoffUnix)
    }
    // Stash the deleted-tickets toggle on config — read by the
    // adapter's count + download paths to pass `exclude_deleted` to
    // Zendesk. Default is "include" (current ingest behavior); we
    // only persist when the operator actively unchecks.
    config.include_deleted_tickets = includeDeletedTickets ? "true" : "false"

    router.post(
      "/settings/onboarding",
      {
        support_platform_connection: {
          provider:             providerKey,
          name,
          config,
          connection_type:      connectionTypeKey,
          settings,
          sync_events_to_valet: syncEventsToValet,
        },
      },
      {
        onSuccess: () => toast.success("Connected to Zendesk. Watch the import progress."),
        onError: (errors) => {
          const first = Object.values(errors)[0] as string | string[] | undefined
          const msg = Array.isArray(first) ? first[0] : first
          toast.error(msg ?? "Connection failed")
        },
        onFinish: () => setSubmitting(false),
      },
    )
  }

  return (
    <Card>
      <form onSubmit={submit} className="space-y-4 p-4">
        <FormField
          label="Brand & Environment"
          required
          hint="Used to label the connection on the Active connections list. Format: Brand + Environment (e.g. Acme Production, Acme Staging)."
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Production"
            required
          />
        </FormField>
        {visibleSchema.length === 0 && (
          <p className="text-sm text-app-muted">No credential fields declared for this provider.</p>
        )}
        {visibleSchema.map((field) => (
          <CredentialFieldInput
            key={field.key}
            field={field}
            value={values[field.key] ?? ""}
            onChange={(v) => setValues((prev) => ({ ...prev, [field.key]: v }))}
          />
        ))}
        {isZendeskApiToken && userIdOptions.length > 0 && (
          <FormField
            label="Map appuser to"
            hint="We'll use this Zendesk user attribute as your customer's identity in Valet. Defaults to External ID. If you store your real user ID in a custom field, pick it here."
          >
            <Select<string>
              value={
                appuserSourceKeyField
                  ? {
                      value: appuserSourceKeyField,
                      label:
                        userIdOptions.find((o) => o.key === appuserSourceKeyField)?.label ??
                        appuserSourceKeyField,
                    }
                  : null
              }
              options={userIdOptions.map((o) => ({ value: o.key, label: o.label }))}
              onChange={(o) => setAppuserSourceKeyField(o?.value ?? "")}
              placeholder="Choose…"
            />
            <p className="mt-1.5 flex items-start gap-1.5 text-[12px] leading-snug text-status-warning">
              <AlertTriangle size={13} className="mt-0.5 flex-none" aria-hidden />
              <span>
                <strong>One-way decision.</strong> Imported tickets and end-users key on this field — pick the
                attribute that already matches your appuser IDs in the SDK / API. Changing it later requires
                a full re-import.
              </span>
            </p>
          </FormField>
        )}

        {/* Custom fields to ingest — opt-in. Default: nothing selected
            (zero CustomFieldDefinitions created at ingest time). Probed
            after credentials check passes; failures hide the section. */}
        {isZendeskApiToken && probeStatus === "ok" && (fieldsLoading || fieldsError || fieldSchemaOptions.length > 0) && (
          <FormField
            label="Custom fields to ingest"
            hint="Select which Zendesk custom fields to import as Valet custom-field definitions. Off by default — only checked fields will be imported. You can change this anytime."
          >
            <CustomFieldsIngestPicker
              fields={fieldSchemaOptions}
              selected={selectedFieldKeys}
              onToggle={toggleFieldKey}
              onSelectAll={() => setSelectedFieldKeys(new Set(fieldSchemaOptions.map((f) => f.key)))}
              onDeselectAll={() => setSelectedFieldKeys(new Set())}
              loading={fieldsLoading}
              error={fieldsError}
            />
          </FormField>
        )}

        {/* Live probe status — driven by the on-change credentials probe.
            "probing" → muted spinner.
            "failed" → red can't-connect banner + button disabled.
            "ok" + count → neutral "Found N tickets"; orange warning if > 100k. */}
        {isZendeskApiToken && probeStatus === "probing" && (
          <div className="flex items-center gap-2 rounded-control border border-border bg-white/[0.02] px-3 py-2 text-sm text-app-muted">
            <RefreshCw size={14} className="animate-spin text-app-muted" aria-hidden />
            Checking Zendesk credentials…
          </div>
        )}
        {isZendeskApiToken && probeStatus === "failed" && (
          <div className="flex items-start gap-2 rounded-control border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-app-text">
            <XCircle size={16} className="mt-0.5 flex-none text-status-error" aria-hidden />
            <div className="flex-1">
              <div><strong className="text-status-error">Cannot connect to Zendesk.</strong></div>
              {probeError && (
                <div className="mt-1 break-all font-mono text-[11px] text-status-error/90">
                  {probeError}
                </div>
              )}
              <div className="mt-1 text-app-muted">
                Double-check the subdomain, agent email, and API token. Save is disabled until the
                credentials probe succeeds.
              </div>
            </div>
          </div>
        )}
        {isZendeskApiToken && probeStatus === "ok" && (probeTicketCount ?? 0) > 0 && (
          <FormField
            label="History to import"
            hint="Pick how far back to import tickets. Comments, attachments, and ticket audits ride along — only tickets within this window come over. Identity (end-users / staff) and config (KB / triggers / macros) are imported in full regardless. Default: All time."
          >
            <Select<string>
              value={{
                value: historyWindowKey,
                label: HISTORY_WINDOWS.find((w) => w.key === historyWindowKey)?.label ?? "All time",
              }}
              options={HISTORY_WINDOWS.map((w) => ({ value: w.key, label: w.label }))}
              onChange={(o) => setHistoryWindowKey(o?.value ?? "all")}
            />
            <label
              htmlFor="include_deleted_tickets"
              className="mt-2 flex cursor-pointer items-start gap-2 text-[13px] text-app-text"
            >
              <input
                id="include_deleted_tickets"
                type="checkbox"
                checked={includeDeletedTickets}
                onChange={(e) => setIncludeDeletedTickets(e.target.checked)}
                className="mt-0.5 flex-none"
              />
              <span className="min-w-0 flex-1">
                Include deleted tickets
                <span className="ml-1 text-[12px] text-app-muted">
                  — uncheck if you want Valet to skip tickets that were soft-deleted in your support platform (e.g. for privacy purges).
                </span>
              </span>
            </label>
          </FormField>
        )}

        {isZendeskApiToken && probeStatus === "ok" && (probeTicketCount ?? 0) > 0 && (() => {
          const baseline   = probeTicketCount ?? 0
          const active     = probeActiveCount
          const deleted    = probeDeletedCount
          // baseline already includes deleted (count_total sums live +
          // deleted server-side). Archived = baseline - active - deleted.
          const archived   = active != null
            ? Math.max(0, baseline - active - (deleted ?? 0))
            : null
          const isWindowed = historyWindowKey !== "all"
          const total      = isWindowed ? (windowedTicketCount ?? baseline) : baseline
          const days       = Math.ceil(total / 100_000)
          const winLabel   = HISTORY_WINDOWS.find((w) => w.key === historyWindowKey)?.label ?? "All time"
          return (
            <div className="space-y-2">
              <div className="rounded-control border border-border bg-white/[0.03] px-3 py-2 text-sm text-app-text">
                {isWindowed ? (
                  windowedProbing ? (
                    <span className="text-app-muted">Counting tickets in the last {winLabel.toLowerCase()}…</span>
                  ) : (
                    <>
                      {total >= baseline ? (
                        <>Found {fmtNumber(total)} tickets — every ticket in this Zendesk falls within the last {winLabel.toLowerCase()}</>
                      ) : (
                        <>Found {fmtNumber(total)} tickets in the last {winLabel.toLowerCase()}</>
                      )}
                      {windowedBreakdown && (
                        <>
                          {" "}({fmtNumber(windowedBreakdown.active)} active + {fmtNumber(windowedBreakdown.archived)} archived
                          {windowedBreakdown.deleted > 0 && <> + {fmtNumber(windowedBreakdown.deleted)} deleted</>}
                          )
                        </>
                      )}
                      {total < baseline && (
                        <span className="text-app-muted">
                          {" "}· {fmtNumber(baseline - total)} older won&rsquo;t be imported
                        </span>
                      )}
                      .
                    </>
                  )
                ) : (
                  <>
                    Found {fmtNumber(total)} tickets in this Zendesk
                    {active != null && archived != null && (
                      <>
                        {" "}({fmtNumber(active)} active + {fmtNumber(archived)} archived
                        {deleted != null && deleted > 0 && (
                          <> + {fmtNumber(deleted)} deleted</>
                        )}
                        )
                      </>
                    )}
                    .
                  </>
                )}
              </div>
              {total > 100_000 && !windowedProbing && (
                <div className="flex items-start gap-2 rounded-control border border-status-warning/40 bg-status-warning/10 px-3 py-2 text-sm text-app-text">
                  <AlertTriangle size={16} className="mt-0.5 flex-none text-status-warning" aria-hidden />
                  <span>
                    Over 100k tickets — at API rates this import will take roughly{" "}
                    {days} {days === 1 ? "day" : "days"}. Switch to the JSON bulk export connection
                    type for a faster initial backfill — the API resumes incremental sync
                    automatically once the bulk ingest completes. Or pick a narrower history window above.
                  </span>
                </div>
              )}
            </div>
          )
        })()}

        {isZendeskApiToken && (
          <label
            htmlFor="sync_events_to_valet"
            className="flex cursor-pointer items-start gap-3 rounded-control border border-border bg-white/[0.02] px-3 py-2.5 hover:bg-white/[0.04]"
          >
            <input
              id="sync_events_to_valet"
              type="checkbox"
              checked={syncEventsToValet}
              onChange={(e) => setSyncEventsToValet(e.target.checked)}
              className="mt-0.5"
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm text-app-text">
                Sync my events to Valet until the integration is completed
              </span>
              <span className="mt-0.5 block text-[12px] leading-snug text-app-muted">
                Keep both systems in sync — new tickets, comments, end-users,
                and knowledge-base updates that happen in your support platform
                are mirrored into Valet in near real-time. We register the
                webhooks for you and tear them down when you finish onboarding.
              </span>
            </span>
          </label>
        )}

        <div className="flex items-center justify-between gap-3 pt-1">
          <TestStatusInline connection={lastConnection ?? null} submitting={submitting} />
          <Button
            type="submit"
            disabled={
              !canSubmit ||
              submitting ||
              (isZendeskApiToken && probeStatus !== "ok") ||
              (isZendeskApiToken && userIdOptions.length > 0 && !appuserSourceKeyField)
            }
          >
            {submitting ? "Saving…" : "Save connection"}
          </Button>
        </div>
      </form>
    </Card>
  )
}

// Inline test-result indicator. Renders red X (with error message) when
// the most recent connection for this provider has last_test_status
// "failed", green check when "ok", and a neutral dash while we have no
// signal. Hidden during submission so the spinner-on-button is the only
// in-flight indicator.
function TestStatusInline({
  connection,
  submitting,
}: {
  connection: ExistingConnection | null
  submitting: boolean
}) {
  if (submitting) return <div className="text-xs text-app-muted">Saving connection…</div>
  if (!connection || !connection.last_tested_at) {
    // No prior test result yet — render nothing rather than a noisy
    // "Not tested yet" label. The Test & save button on the right is
    // already the obvious primary action.
    return null
  }
  if (connection.last_test_status === "ok") {
    return (
      <div className="flex items-center gap-1.5 text-xs text-status-success">
        <CheckCircle2 size={14} />
        Verified — last tested {fmtCompactRelative(connection.last_tested_at)}
      </div>
    )
  }
  if (connection.last_test_status === "failed") {
    return (
      <div className="flex items-start gap-1.5 text-xs text-status-error">
        <XCircle size={14} className="mt-0.5 shrink-0" />
        <span>
          Test failed{connection.last_test_error ? `: ${connection.last_test_error}` : ""} ·
          {" "}{fmtCompactRelative(connection.last_tested_at)}
        </span>
      </div>
    )
  }
  return null
}

function CredentialFieldInput({
  field,
  value,
  onChange,
}: {
  field: CredentialField
  value: string
  onChange: (v: string) => void
}) {
  // Schema choices may be either string[] (Ruby symbol arrays) or {value,label}[]
  const normalizedChoices: Array<{ value: string; label: string }> = useMemo(() => {
    const raw = (field.choices ?? []) as unknown[]
    return raw.map((c) => {
      if (typeof c === "string") return { value: c, label: c.replace(/_/g, " ") }
      return c as { value: string; label: string }
    })
  }, [field.choices])

  const isSecret = field.type === "password" || field.type === "secret"

  return (
    <FormField
      label={field.label || field.key}
      required={field.required}
      hint={field.helperText ?? field.helper_text ?? undefined}
    >
      {field.type === "select" ? (
        <Select<string>
          value={
            value
              ? { value, label: normalizedChoices.find((c) => c.value === value)?.label ?? value }
              : null
          }
          options={normalizedChoices}
          onChange={(o) => onChange(o?.value ?? "")}
          placeholder="Choose…"
        />
      ) : (
        <Input
          type={isSecret ? "password" : "text"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={isSecret ? "new-password" : "off"}
          placeholder={field.default != null && field.default !== "" ? String(field.default) : undefined}
        />
      )}
    </FormField>
  )
}

// Compact test-result chip for the Monitor card header. Distinct from
// TestStatusInline (which lives on the Initialize tab) — this one is a
// single-line pill rendered next to the lifecycle badge.
function TestStatusChip({ connection }: { connection: ExistingConnection }) {
  if (!connection.last_tested_at) {
    return (
      <span className="inline-flex items-center gap-1 rounded-chip border border-border bg-white/[0.02] px-2 py-0.5 text-[11px] text-app-muted">
        <Circle size={10} /> untested
      </span>
    )
  }
  if (connection.last_test_status === "ok") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-chip border border-status-success/30 bg-status-success/10 px-2 py-0.5 text-[11px] text-status-success"
        title={`Verified ${fmtCompactRelative(connection.last_tested_at)}`}
      >
        <CheckCircle2 size={10} /> verified
      </span>
    )
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-chip border border-status-error/30 bg-status-error/10 px-2 py-0.5 text-[11px] text-status-error"
      title={connection.last_test_error ?? "Test failed"}
    >
      <XCircle size={10} /> test failed
    </span>
  )
}

// OAuth and bulk-upload onboarding flows are not yet wired in the
// portal. The placeholder cards below render only when an adapter
// declares one of those connection_flows; the credential-form path is
// the live one for Zendesk today.
function OauthRedirectBranch({ providerName }: { providerKey: string; providerName: string }) {
  return (
    <Card>
      <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full border border-border bg-white/5">
          <Lock size={20} />
        </div>
        <div>
          <h3 className="text-base font-semibold text-app-text">OAuth not yet available for {providerName}</h3>
          <p className="mt-1 max-w-sm text-sm text-app-muted">
            Use API token / bulk upload while OAuth is being wired in.
          </p>
        </div>
      </div>
    </Card>
  )
}

function BulkUploadBranch() {
  return (
    <Card>
      <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full border border-border bg-white/5">
          <Upload size={20} />
        </div>
        <div>
          <h3 className="text-base font-semibold text-app-text">Bulk upload</h3>
          <p className="mt-1 max-w-sm text-sm text-app-muted">
            File-based onboarding isn't wired in the portal yet. Use the API-token flow for now.
          </p>
        </div>
      </div>
    </Card>
  )
}

// API + Bulk-export hybrid branch. Two-stage flow keyed off whether
// the connection is already saved + verified:
//
//   Stage 1 (no lastConnection OR last_test_status !== "ok"):
//     show CredentialFormBranch — operator hasn't proven API works yet.
//
//   Stage 2 (lastConnection exists AND last_test_status === "ok"):
//     show a compact "API connected" summary + the BulkImportUrlCard.
//     The "Edit credentials" link drops the operator back to Stage 1
//     locally so they can re-submit the credential form if needed.
//
// On successful URL submit we toast + flip the top-level Onboarding tab
// to `monitor` via onConnected (already plumbed by InitializeTab).
function CsvPlusApiBranch({
  schema,
  providerKey,
  lastConnection,
  onConnected,
}: {
  schema: CredentialField[]
  providerKey: string
  lastConnection: ExistingConnection | null
  onConnected: () => void
}) {
  const isVerified = !!(lastConnection && lastConnection.last_test_status === "ok")
  const [forceEdit, setForceEdit] = useState(false)
  const [activeStep, setActiveStep] = useState<"connection" | "csvs">("connection")

  // Auto-advance to Step 2 the first time the connection becomes verified.
  // Re-running this on every render is fine — useEffect dedupes by deps.
  useEffect(() => {
    if (isVerified && !forceEdit) setActiveStep("csvs")
  }, [isVerified, forceEdit])

  const onConnectionTabClick = () => setActiveStep("connection")
  const onCsvsTabClick = () => {
    if (isVerified) setActiveStep("csvs")
  }

  return (
    <div className="space-y-4">
      <StepTabBar
        activeStep={activeStep}
        isVerified={isVerified}
        onConnectionClick={onConnectionTabClick}
        onCsvsClick={onCsvsTabClick}
      />

      {activeStep === "connection" && (
        <div className="space-y-4">
          {isVerified && !forceEdit ? (
            <ApiConnectedSummaryCard
              connection={lastConnection!}
              onEditCredentials={() => {
                setForceEdit(true)
                setActiveStep("connection")
              }}
            />
          ) : (
            <CredentialFormBranch
              schema={schema}
              providerKey={providerKey}
              connectionTypeKey="csv"
              lastConnection={lastConnection}
            />
          )}
        </div>
      )}

      {activeStep === "csvs" && isVerified && (
        <div className="space-y-4">
          <ForwardExportEmailCard connection={lastConnection!} />
          <BulkImportUrlCard
            connectionId={lastConnection!.id}
            onConnected={onConnected}
          />
        </div>
      )}
    </div>
  )
}

// Two-tab step indicator for the API + Bulk-export flow. Step 1 is
// always reachable; Step 2 is locked (cursor + tooltip) until the
// connection has been verified, at which point it auto-activates.
function StepTabBar({
  activeStep,
  isVerified,
  onConnectionClick,
  onCsvsClick,
}: {
  activeStep: "connection" | "csvs"
  isVerified: boolean
  onConnectionClick: () => void
  onCsvsClick: () => void
}) {
  return (
    <div className="flex items-stretch overflow-hidden rounded-control border border-border bg-white/[0.02]">
      <StepTab
        index={1}
        label="Connection"
        active={activeStep === "connection"}
        complete={isVerified}
        disabled={false}
        onClick={onConnectionClick}
      />
      <div className="w-px bg-border" aria-hidden />
      <StepTab
        index={2}
        label="JSON exports"
        active={activeStep === "csvs"}
        complete={false}
        disabled={!isVerified}
        onClick={onCsvsClick}
        disabledHint="Complete Step 1 first"
      />
    </div>
  )
}

function StepTab({
  index,
  label,
  active,
  complete,
  disabled,
  onClick,
  disabledHint,
}: {
  index: number
  label: string
  active: boolean
  complete: boolean
  disabled: boolean
  onClick: () => void
  disabledHint?: string
}) {
  const baseClass = "flex flex-1 items-center gap-2 px-4 py-2.5 text-left text-sm transition"
  const stateClass = active
    ? "bg-white/[0.05] text-app-text"
    : disabled
      ? "cursor-not-allowed text-app-muted/60"
      : "text-app-muted hover:bg-white/[0.03] hover:text-app-text cursor-pointer"

  return (
    <button
      type="button"
      className={`${baseClass} ${stateClass}`}
      onClick={disabled ? undefined : onClick}
      title={disabled ? disabledHint : undefined}
      aria-current={active ? "step" : undefined}
      aria-disabled={disabled || undefined}
    >
      <span
        className={`flex h-5 w-5 flex-none items-center justify-center rounded-full text-[11px] font-semibold ${
          complete
            ? "bg-status-success/20 text-status-success"
            : active
              ? "bg-white/10 text-app-text"
              : "bg-white/5 text-app-muted"
        }`}
        aria-hidden
      >
        {complete ? <Check size={12} strokeWidth={3} /> : index}
      </span>
      <span className="font-medium">Step {index} — {label}</span>
    </button>
  )
}

// Small "Or forward Zendesk's email" card. Sits ABOVE the URL textarea
// as the primary CTA — the operator forwards Zendesk's "your export is
// ready" email to the per-connection plus-addressed inbox shown here,
// and `BulkImportMailbox` extracts the URLs and queues them through the
// same Job1+Job2 fan-in as the textarea path. Both options coexist.
function ForwardExportEmailCard({ connection }: { connection: ExistingConnection }) {
  const [copied, setCopied] = useState(false)
  const address = connection.import_email_address

  const onCopy = async () => {
    try {
      await navigator.clipboard?.writeText(address)
      setCopied(true)
      toast.success("Email address copied")
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error("Could not copy — copy the address manually")
    }
  }

  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-control border border-border bg-white/5 text-app-muted">
          <Mail size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-app-text">Or forward Zendesk&rsquo;s JSON export email</h3>
          <p className="mt-1 text-sm text-app-muted">
            Forward Zendesk&rsquo;s JSON export email to the address below. We&rsquo;ll
            extract the URLs automatically and queue the import. Send the
            same email any time you receive a new export window.
          </p>

          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 select-all overflow-x-auto rounded-control border border-border bg-app-panel/40 px-3 py-2 font-mono text-xs text-app-text">
              {address}
            </code>
            <button
              type="button"
              onClick={onCopy}
              className="inline-flex items-center gap-1 rounded-control border border-border px-2 py-2 text-[11px] text-app-muted hover:bg-white/5"
              title="Copy address"
            >
              <Copy size={12} />
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      </div>
    </Card>
  )
}

// Compact "API connected" card shown once the underlying API connection
// has been tested OK. Mirrors the look of TestStatusInline + adds a
// per-stage "Edit credentials" affordance that drops the operator back
// to the credential form (no real edit mutation — the existing form's
// re-submit already overwrites the connection's config).
function ApiConnectedSummaryCard({
  connection,
  onEditCredentials,
}: {
  connection: ExistingConnection
  onEditCredentials: () => void
}) {
  const subdomain = connection.provider_account_name ?? null
  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-control border border-status-success/30 bg-status-success/10 text-status-success">
          <CheckCircle2 size={18} />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-app-text">
              API connected{connection.name ? ` — ${connection.name}` : ""}
            </h3>
            <button
              type="button"
              onClick={onEditCredentials}
              className="text-xs text-app-muted underline-offset-2 hover:text-app-text hover:underline"
            >
              Edit credentials
            </button>
          </div>
          {subdomain && (
            <p className="mt-0.5 text-xs text-app-muted">{subdomain}</p>
          )}
          <p className="mt-1 text-xs text-status-success">
            Verified — last tested{" "}
            {connection.last_tested_at ? fmtCompactRelative(connection.last_tested_at) : "just now"}
          </p>
        </div>
      </div>
    </Card>
  )
}

// Bulk import URL paste card — the operator pastes one or more
// download URLs Zendesk emailed them, one per line. Valet downloads
// each file directly from Zendesk's host, so the operator never has to
// download a multi-GB ZIP locally.
//
// Multi-URL support is required because Zendesk forces accounts > 1M
// tickets into 31-day-window exports — one email per window. Each line
// is validated independently against the host allowlist; submit is
// disabled until every line passes.
//
// On submit POSTs `{ download_urls: [...] }` to
// /support_connections/:id/bulk_import_url. Server-side
// (Portal::SupportConnectionsController#bulk_import_url) creates one
// Job1 per URL (kicked off immediately) plus a single dependent Job2
// that waits for all of them.
const ALLOWED_HOST_SUFFIXES = [
  "zendesk.com",
  "zendesk-export.com",
  "amazonaws.com",
  "cloudfront.net",
]

type LineCheck = { line: string; ok: boolean; error: string | null }

function checkUrlLine(line: string): LineCheck {
  const trimmed = line.trim()
  if (trimmed === "") return { line: trimmed, ok: false, error: "Empty line" }

  let parsed: URL
  try {
    parsed = new URL(trimmed)
  } catch {
    return { line: trimmed, ok: false, error: "Not a valid URL" }
  }

  if (parsed.protocol !== "https:") {
    return { line: trimmed, ok: false, error: "Must use https://" }
  }

  const host = parsed.hostname.toLowerCase()
  const ok = ALLOWED_HOST_SUFFIXES.some(
    (suffix) => host === suffix || host.endsWith("." + suffix)
  )
  if (!ok) {
    return { line: trimmed, ok: false, error: `Host ${host} is not on the allowlist` }
  }

  return { line: trimmed, ok: true, error: null }
}

function BulkImportUrlCard({
  connectionId,
  onConnected,
}: {
  connectionId: number
  onConnected: () => void
}) {
  const [text, setText] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const checks = useMemo<LineCheck[]>(() => {
    return text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map(checkUrlLine)
  }, [text])

  const validLines = checks.filter((c) => c.ok)
  const invalidLines = checks.filter((c) => !c.ok)
  const submittable = validLines.length > 0 && invalidLines.length === 0 && !submitting

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!submittable) return
    setSubmitting(true)
    router.post(
      `/support_connections/${connectionId}/bulk_import_url`,
      { download_urls: validLines.map((c) => c.line) },
      {
        preserveScroll: true,
        onSuccess: () => {
          toast.success("Import queued — watch the Monitor tab")
          onConnected()
        },
        onError: (errors) => {
          const first = Object.values(errors)[0] as string | string[] | undefined
          const msg = Array.isArray(first) ? first[0] : first
          toast.error(msg ?? "Could not start bulk import")
        },
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <Card className="p-4">
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-control border border-border bg-white/5 text-app-muted">
            <Download size={18} />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-app-text">Paste Zendesk export URL</h3>
            <p className="mt-1 text-sm text-app-muted">
              Open the email Zendesk sent when you requested the JSON export.
              Copy the download link and paste it here. Valet will fetch the
              file directly from Zendesk&rsquo;s servers.
            </p>
            <p className="mt-2 text-xs text-app-muted">
              Zendesk splits very large accounts (&gt; 1 million tickets) into
              multiple separate exports &mdash; one email per 31-day window.
              Paste every URL you received, one per line. Valet will queue all
              of them and stream them in parallel; order doesn&rsquo;t matter.
            </p>
          </div>
        </div>

        <div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            placeholder={"https://...\nhttps://..."}
            className="block w-full rounded-control border border-border bg-white/5 px-3 py-2 font-mono text-xs text-app-text placeholder:text-app-muted/60 focus:outline-none focus:ring-1 focus:ring-app-text/30"
          />
          <p className="mt-1 text-[11px] text-app-muted">
            URLs from <code>*.zendesk.com</code>, <code>*.zendesk-export.com</code>, and
            AWS download hosts (<code>*.amazonaws.com</code>, <code>*.cloudfront.net</code>) are accepted.
          </p>
        </div>

        {checks.length > 0 && (
          <div className="space-y-1">
            {checks.map((c, idx) => (
              <div
                key={idx}
                className={cn(
                  "flex items-start gap-2 rounded-chip border px-2 py-1 text-xs",
                  c.ok
                    ? "border-status-success/30 bg-status-success/5 text-status-success"
                    : "border-status-error/30 bg-status-error/5 text-status-error"
                )}
              >
                {c.ok ? <CheckCircle2 size={12} className="mt-[2px]" /> : <AlertTriangle size={12} className="mt-[2px]" />}
                <span className="flex-1 break-all font-mono">
                  Line {idx + 1}: {truncateUrl(c.line)}
                </span>
                {!c.ok && <span className="font-medium">{c.error}</span>}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-app-muted">
            {validLines.length === 0
              ? "Paste at least one URL"
              : `${validLines.length} URL${validLines.length === 1 ? "" : "s"} ready`}
          </span>
          <Button type="submit" disabled={!submittable}>
            {submitting ? "Starting…" : "Start import"}
          </Button>
        </div>
      </form>
    </Card>
  )
}

function truncateUrl(url: string): string {
  if (url.length <= 80) return url
  return url.slice(0, 60) + "…" + url.slice(-15)
}

// ============================================================
// Tab 2 — Monitor
// ============================================================

function MonitorTab({
  connections,
  canEdit,
  supportImportJob,
}: {
  connections:   ExistingConnection[]
  canEdit:       boolean
  supportImportJob: SupportImportJob | null
}) {
  // Polling: 5s while any connection has a running run, 30s otherwise.
  // Imports run for hours; per-page progress moves at fractions of 1 Hz, so
  // a 1s tick was pure write-amplification on the controller (~3,600 hits/h
  // for data that changes ~7 times/min). 5s is still well below human-perceived
  // staleness, ~12x cheaper.
  useEffect(() => {
    const anyRunning = connections.some((c) =>
      c.runs.some((r) => r.state === "running" || r.state === "pending"),
    )
    const interval = anyRunning ? 5000 : 30000
    const tick = setInterval(() => {
      router.reload({ only: ["existing_connections"] })
    }, interval)
    return () => clearInterval(tick)
  }, [connections])

  if (connections.length === 0) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full border border-border bg-white/5 text-app-muted">
            <Plug size={20} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-app-text">No connection set up yet</h3>
            <p className="mt-1 max-w-md text-sm text-app-muted">
              Head to the Initialize tab to connect Zendesk or another support platform.
            </p>
          </div>
          <Button onClick={() => router.visit("/settings/onboarding?tab=initialize")}>
            Go to Initialize
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {supportImportJob && (
        <Card className="p-4">
          <div className="mb-2 text-xs uppercase tracking-wide text-app-muted">Onboarding progress</div>
          <OnboardingRunPhaseStrip phases={supportImportJob.phases} />
        </Card>
      )}
      {connections.map((c) => (
        <ConnectionMonitorCard key={c.id} connection={c} canEdit={canEdit} />
      ))}
    </div>
  )
}

function ConnectionMonitorCard({ connection, canEdit }: { connection: ExistingConnection; canEdit: boolean }) {
  const [busy, setBusy] = useState<string | null>(null)
  const [showCutover, setShowCutover] = useState(false)
  const [showFullDownload, setShowFullDownload] = useState(false)
  const [showDelete, setShowDelete] = useState(false)

  const csrfPost = async (path: string) => {
    const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
    return fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
    })
  }

  const run = async (
    action: "pause" | "resume" | "disconnect" | "test" | "start_import" | "reingest_from_raw" | "destroy",
  ) => {
    setBusy(action)
    try {
      const url = action === "destroy"
        ? `/support_connections/${connection.id}`
        : `/support_connections/${connection.id}/${action}`
      const csrf = (document.querySelector("meta[name='csrf-token']") as HTMLMetaElement | null)?.content || ""
      const res = action === "destroy"
        ? await fetch(url, {
            method: "DELETE",
            credentials: "same-origin",
            headers: { Accept: "application/json", "X-CSRF-Token": csrf },
          })
        : await csrfPost(url)
      const ok = res.ok || res.redirected || res.status < 400
      if (ok) {
        if (action === "pause") toast.success("Paused")
        else if (action === "resume") toast.success("Resumed")
        else if (action === "destroy") {
          toast.success("Connection deletion queued — imported data will be removed in the background.")
        }
        else if (action === "disconnect") {
          let appusersWiped = 0
          let staffWiped    = 0
          try {
            const ct = res.headers.get("content-type") || ""
            if (ct.includes("application/json")) {
              const body = (await res.clone().json()) as {
                appusers_wiped?: number
                company_users_wiped?: number
              }
              appusersWiped = body.appusers_wiped ?? 0
              staffWiped    = body.company_users_wiped ?? 0
            }
          } catch {
            // non-JSON or parse error: fall through with zeroes
          }
          const total = appusersWiped + staffWiped
          if (total > 0) {
            toast.success(
              `Disconnected — wiped ${appusersWiped} user + ${staffWiped} staff imported references.`,
            )
          } else {
            toast.success("Disconnected — no imported references to wipe.")
          }
        }
        else if (action === "test") toast.success("Connection verified")
        else if (action === "start_import") toast.success("Download started")
        else if (action === "reingest_from_raw") toast.success("Re-ingest started")
      } else if (action === "test") {
        toast.error("Test connection failed")
      } else if (action === "start_import") {
        toast.error("Could not start download")
      } else if (action === "reingest_from_raw") {
        toast.error("Could not start re-ingest")
      } else if (action === "destroy") {
        toast.error("Could not delete connection")
      } else {
        toast.error(`${action} failed`)
      }
    } catch {
      if (action === "start_import") toast.error("Could not start download")
      else if (action === "reingest_from_raw") toast.error("Could not start re-ingest")
      else if (action === "destroy") toast.error("Could not delete connection")
      else toast.error(`${action} failed`)
    } finally {
      setBusy(null)
      router.reload({ only: ["existing_connections"] })
    }
  }

  const phase1Successful = connection.runs.some(
    (r) => r.kind === "download_tickets" && r.state === "completed",
  )
  const phase1Active = connection.runs.some(
    (r) => r.kind === "download_tickets" && (r.state === "running" || r.state === "pending"),
  )
  const importActive = connection.runs.some((r) => r.state === "running" || r.state === "pending")
  // Backwards-compatible aliases for downstream conditionals.
  const phase1Done   = phase1Successful

  const isDisconnected = connection.lifecycle_state === "disconnected"

  return (
    <Card className="overflow-hidden p-0">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-control bg-white/5 text-app-muted">
            <Plug size={16} />
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-app-text">
              {connection.name || connection.key || connection.provider_display_name}
              <Badge tone={LIFECYCLE_TONE[connection.lifecycle_state]}>{connection.lifecycle_state}</Badge>
              {connection.paused && <Badge tone="warning">paused</Badge>}
            </div>
            <div className="mt-0.5 text-[11px] text-app-muted">
              {connection.provider_display_name}
              {connection.last_sync_at ? ` · last sync ${fmtCompactRelative(connection.last_sync_at)}` : " · never synced"}
              {connection.disconnected_at ? ` · disconnected ${fmtCompactRelative(connection.disconnected_at)}` : ""}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <TestStatusChip connection={connection} />
          {canEdit && !isDisconnected && (
            <Button variant="ghost" size="sm" onClick={() => run("test")} disabled={busy === "test"}>
              <RefreshCw size={14} className={cn("mr-1.5", busy === "test" && "animate-spin")} />
              {busy === "test" ? "Testing…" : "Re-test"}
            </Button>
          )}
          {canEdit && !isDisconnected && !connection.paused && (
            <Button variant="ghost" size="sm" onClick={() => run("pause")} disabled={busy === "pause"}>
              <Pause size={14} className="mr-1.5" />
              {busy === "pause" ? "Pausing…" : "Pause active sync"}
            </Button>
          )}
          {canEdit && !isDisconnected && connection.paused && (
            <Button variant="ghost" size="sm" onClick={() => run("resume")} disabled={busy === "resume"}>
              <Play size={14} className="mr-1.5" />
              {busy === "resume" ? "Resuming…" : "Resume active sync"}
            </Button>
          )}
          {canEdit && !isDisconnected && !phase1Successful && !phase1Active && (
            <Button size="sm" onClick={() => setShowFullDownload(true)} disabled={busy === "start_import"}>
              <Sparkles size={14} className="mr-1.5" />
              {busy === "start_import" ? "Starting…" : "Full Download"}
            </Button>
          )}
          {canEdit && !isDisconnected && phase1Done && !importActive && (
            <Button size="sm" onClick={() => run("reingest_from_raw")} disabled={busy === "reingest_from_raw"}>
              <RefreshCw size={14} className={cn("mr-1.5", busy === "reingest_from_raw" && "animate-spin")} />
              {busy === "reingest_from_raw" ? "Starting…" : "Re-ingest from raw"}
            </Button>
          )}
          {/* This view IS the manage UI — there is no standalone
              /support_connections/:id page. */}
        </div>
      </header>

      {!isDisconnected && (connection.last_count_total ?? 0) > 0 && (
        <div className="px-5 pt-4">
          <MonitorCountBanner connection={connection} />
        </div>
      )}

      {isDisconnected ? (
        <div className="px-5 py-4">
          <div className="rounded-control border border-border bg-white/[0.02] px-4 py-3 text-sm text-app-muted">
            Disconnected{connection.disconnected_at ? ` on ${new Date(connection.disconnected_at).toLocaleDateString()}` : ""}.
            {connection.raw_purge_due_at &&
              ` Raw bundle cleanup scheduled for ${new Date(connection.raw_purge_due_at).toLocaleDateString()} (+30d).`}
            {" Imported tickets remain queryable; agent and bootstrap-derived skills stay live."}
          </div>
        </div>
      ) : (
        <>
          <div className="px-5 py-4">
            <div className="mb-2 text-xs uppercase tracking-wide text-app-muted">Recent runs</div>
            {connection.runs.length === 0 ? (
              <div className="rounded-control border border-dashed border-border px-4 py-6 text-center text-sm text-app-muted">
                {!phase1Successful && !phase1Active && connection.lifecycle_state === "connecting"
                  ? "Connection verified. Click Full Download to pull historical data."
                  : phase1Done && !importActive
                  ? "Phase 1 complete. Click Re-ingest from raw if you need to re-process bundles."
                  : "No runs yet — wait a moment for the first sync to start."}
              </div>
            ) : (
              <RunsTable
                runs={connection.runs}
                providerDisplayName={connection.provider_display_name}
                connectionId={connection.id}
                canEdit={canEdit}
              />
            )}
          </div>

          {canEdit && connection.provider === "zendesk" && (
            <WebhookStatusBlock connection={connection} />
          )}

          {canEdit && <RateLimitBlock connection={connection} />}

          {canEdit && (
            <div className="border-t border-border bg-status-error/[0.05] px-5 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-status-error">Mark as fully migrated</div>
                  <div className="mt-0.5 text-xs text-app-muted">
                    Stops all sync. Imported tickets and agent stay live; raw bundles GC at +30 days.
                    This cannot be undone via the portal.
                  </div>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => setShowCutover(true)}
                  className="border border-status-error/40 text-status-error hover:bg-status-error/10"
                >
                  <PowerOff size={14} className="mr-1.5" />
                  Mark as fully migrated — disconnect {connection.provider_display_name}
                </Button>
              </div>
            </div>
          )}
          {canEdit && (
            <div className="border-t border-border bg-status-error/[0.05] px-5 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-status-error">Delete connection</div>
                  <div className="mt-0.5 text-xs text-app-muted">
                    Hard-delete: removes the connection plus every imported ticket, comment,
                    end-user and raw bundle. If the import is still running, all in-flight work is lost.
                    This cannot be undone.
                  </div>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => setShowDelete(true)}
                  disabled={connection.lifecycle_state === "deleting"}
                  className="border border-status-error/40 text-status-error hover:bg-status-error/10"
                >
                  <Trash2 size={14} className="mr-1.5" />
                  {connection.lifecycle_state === "deleting" ? "Deleting…" : `Delete ${connection.provider_display_name}`}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <CutoverDialog
        open={showCutover}
        onClose={() => setShowCutover(false)}
        connection={connection}
        onConfirm={async () => {
          setShowCutover(false)
          await run("disconnect")
        }}
      />

      <DeleteConnectionDialog
        open={showDelete}
        onClose={() => setShowDelete(false)}
        connection={connection}
        onConfirm={async () => {
          setShowDelete(false)
          await run("destroy")
        }}
      />

      <FullDownloadDialog
        open={showFullDownload}
        onClose={() => setShowFullDownload(false)}
        connection={connection}
        onConfirm={async () => {
          setShowFullDownload(false)
          await run("start_import")
        }}
      />
    </Card>
  )
}

// "What we pulled from <provider>" checklist. One row per known
// resource kind; download_state and import_state each render as their
// own small phase icon (Download arrow + Database) so operators can
// see which phase is in flight. Renders below the in-flight progress
// strip and above the Recent runs table on the Monitor card.
const RESOURCE_LABELS: Record<string, string> = {
  tickets:       "Tickets",
  comments:      "Comments",
  audits:        "Audit history",
  kb_articles:   "Knowledge Base",
  staff_users:   "Staff users",
  end_users:     "End-users",
  brands:        "Brands",
  groups:        "Groups",
  sla_policies:  "SLA policies",
  triggers:      "Triggers",
  macros:        "Macros",
  automations:   "Automations",
  field_schemas: "Field schemas",
}

function ResourceChecklist({
  resources,
  providerDisplayName,
}: {
  resources: ResourceStatus[]
  providerDisplayName: string
}) {
  const [open, setOpen] = useState(true)
  if (resources.length === 0) return null

  return (
    <div className="border-b border-border px-5 py-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mb-2 flex items-center gap-1.5 text-xs uppercase tracking-wide text-app-muted hover:text-app-text"
      >
        <ChevronRight size={12} className={cn("transition-transform", open && "rotate-90")} />
        What we pulled from {providerDisplayName}
      </button>
      {open && (
        <ul className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
          {resources.map((r) => (
            <ResourceChecklistRow key={r.resource_kind} resource={r} />
          ))}
        </ul>
      )}
    </div>
  )
}

// Per-phase color mapping. Both download and import icons use the same
// scheme so operators can read the row left-to-right as "fetch → store".
function phaseIconClass(state: ResourceState): string {
  switch (state) {
    case "succeeded": return "text-status-success"
    case "running":   return "text-status-warning animate-pulse"
    case "failed":    return "text-status-error"
    case "skipped":   return "text-app-muted"
    case "pending":
    default:          return "text-app-muted/40"
  }
}

function phaseTooltip(label: string, state: ResourceState): string {
  return `${label}: ${state}`
}

// Phase strip — vertical list of JobPhase rows shown inside the
// expanded Recent runs row, above the per-resource checklist. Adapters
// without a phase state machine emit an empty `phases` array; the strip
// renders nothing in that case (and the legacy resource checklist
// stays in place as the sole progress surface).
function PhaseStrip({
  phases,
  connectionId,
  runId,
  reconciler,
  ingestVerification,
}: {
  phases: JobPhase[]
  // When omitted (legacy callers), the (i) modal trigger is hidden but
  // counts/labels still render. Threading both through enables the
  // operator to inspect per-resource error detail.
  connectionId?: number
  runId?: number
  reconciler?: ReconcilerProgress | null
  ingestVerification?: IngestVerification | null
}) {
  if (!phases || phases.length === 0) return null

  const sorted = [...phases].sort((a, b) => a.slot - b.slot)

  return (
    <div className="border-b border-border px-5 py-4">
      <div className="mb-2 text-xs uppercase tracking-wide text-app-muted">Phases</div>
      <ul className="space-y-1.5 text-sm">
        {sorted.map((p) => (
          <PhaseRow
            key={p.slot}
            phase={p}
            connectionId={connectionId}
            runId={runId}
            reconciler={p.slot === 2 ? reconciler : null}
            ingestVerification={p.slot === 2 ? ingestVerification : null}
          />
        ))}
      </ul>
    </div>
  )
}

function OnboardingRunPhaseStrip({ phases }: { phases: JobPhase[] }) {
  return <PhaseStrip phases={phases ?? []} />
}

function PhaseRow({
  phase,
  connectionId,
  runId,
  reconciler,
  ingestVerification,
}: {
  phase: JobPhase
  connectionId?: number
  runId?: number
  reconciler?: ReconcilerProgress | null
  ingestVerification?: IngestVerification | null
}) {
  // Multi-item phases (e.g. Phase 1 = tickets + comments downloading in
  // parallel) render the title row + one sub-row per item underneath.
  const items = phase.items && phase.items.length > 1 ? phase.items : null

  // Visual completion can lead the backend phase row by one
  // AdvanceJobPhase tick: the moment every sub-stream hits 100% the
  // operator expects the icon to flip to "done", but the backend
  // phase row's `state` only flips after the next tick fires
  // `complete_when`. Treat "running + all items at 100%" as visually
  // completed so the icon agrees with the per-item bars. NB: there's
  // intentionally NO phase-level aggregate header detail when items
  // are rendered — each stream has its own distinct progress.
  const allItemsDone =
    items != null &&
    items.every((it) => {
      const pct = it.cursor_percent ?? it.percent
      return pct != null && pct >= 100
    })
  const streamVisualState: JobPhase["state"] =
    phase.state === "running" && allItemsDone ? "completed" : phase.state
  const verificationApplies = phase.slot === 2 && streamVisualState === "completed"
  const verificationPassed = ingestVerification?.ok === true
  const visualState: JobPhase["state"] =
    verificationApplies && !verificationPassed
      ? (ingestVerification ? "failed" : "running")
      : streamVisualState

  // Sub-row state mirrors the header's visual state — otherwise an
  // in-progress bar (warning color) lingers under a green check.
  const visualItemState: JobPhase["state"] =
    streamVisualState === "completed" ? "completed" : phase.state
  const itemsHaveIssues =
    items != null &&
    items.some((it) => it.state === "failed" || (it.errors_count ?? 0) > 0)
  const autoCollapseItems =
    phase.slot === 1 &&
    items != null &&
    visualState === "completed" &&
    allItemsDone &&
    !itemsHaveIssues
  const [itemRowsCollapsed, setItemRowsCollapsed] = useState(autoCollapseItems)
  const [itemRowsTouched, setItemRowsTouched] = useState(false)

  useEffect(() => {
    if (!autoCollapseItems) {
      setItemRowsCollapsed(false)
      return
    }
    if (!itemRowsTouched) setItemRowsCollapsed(true)
  }, [autoCollapseItems, itemRowsTouched])

  const toggleItemRows = () => {
    setItemRowsTouched(true)
    setItemRowsCollapsed((prev) => !prev)
  }

  return (
    <li className="space-y-0.5">
      <div className="flex items-center gap-2">
        {autoCollapseItems && (
          <button
            type="button"
            onClick={toggleItemRows}
            className="flex h-4 w-4 flex-none items-center justify-center rounded-control text-app-muted hover:bg-white/5 hover:text-app-text"
            aria-label={itemRowsCollapsed ? "Expand Phase 1 details" : "Collapse Phase 1 details"}
          >
            {itemRowsCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
          </button>
        )}
        <PhaseStateIcon state={visualState} />
        <span className="flex-1 text-app-text">
          <span className="text-app-muted">Phase {phase.slot}</span>
          <span className="text-app-muted"> · </span>
          <span>{phase.label}</span>
        </span>
        <span className="flex-none text-xs text-app-muted tabular-nums">
          {items
            ? (itemRowsCollapsed ? `${items.length} resources · 100%` : null)
            : <PhaseRightDetail phase={phase} />}
        </span>
      </div>
      {items && !itemRowsCollapsed && (
        <ul className="ml-6 space-y-2 text-xs text-app-muted">
          {items.map((it) => {
            // Per-item state wins over the phase-level state when the
            // backend marked this resource :failed. That's how a single
            // failed resource (e.g. Comments) gets a red bar + triangle
            // even while the phase row itself is still "running".
            const itemState: JobPhase["state"] = it.state === "failed" ? "failed" : visualItemState
            const errorsCount = it.errors_count ?? 0
            return (
              <li key={it.label} className="space-y-1 tabular-nums">
                <div className="flex items-center gap-2">
                  <span className="flex-1 text-app-text">{it.label}</span>
                  {errorsCount > 0 && (
                    <PhaseItemErrorsBadge
                      count={errorsCount}
                      aspectKind={it.aspect_kind}
                      label={it.label}
                      connectionId={connectionId}
                      runId={runId}
                    />
                  )}
                  {it.state === "failed" && (
                    <AlertTriangle
                      size={13}
                      className="flex-none text-status-error"
                      aria-label={it.error_message ?? "Failed"}
                    />
                  )}
                  <span className="flex-none">
                    <PhaseItemDetail item={it} state={itemState} />
                  </span>
                </div>
                <PhaseProgressBar item={it} state={itemState} />
                {it.state === "failed" && it.error_message && (
                  <div className="text-[11px] text-status-error">
                    {it.error_message}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {reconciler && (
        <ul className="ml-6 mt-2 space-y-2 text-xs text-app-muted">
          <ReconcilerProgressItem progress={reconciler} />
        </ul>
      )}
      {ingestVerification && (
        <div className="ml-6 mt-2">
          <IngestVerificationBlock
            report={ingestVerification}
            phases={[phase]}
            connectionId={connectionId}
            runId={runId}
          />
        </div>
      )}
    </li>
  )
}

// Single-row progress bar for a phase-1 stream. Picks cursor_percent
// when available (cursor-time-position is the honest signal during
// imports of growing tenants), falls back to count-based percent.
// Hidden entirely on pending/idle so we don't flash an empty rail.
function PhaseProgressBar({
  item,
  state,
}: {
  item: NonNullable<JobPhase["items"]>[number]
  state: JobPhase["state"]
}) {
  const pct = item.cursor_percent ?? item.percent ?? 0
  if (state === "pending" || state === "skipped") return null
  const fillColor =
    state === "completed" ? "bg-status-success" :
    state === "failed" || state === "cancelled" ? "bg-status-error" :
    "bg-status-warning"
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/[0.06]">
      <div
        className={cn("h-full rounded-full transition-all duration-500", fillColor)}
        style={{ width: `${Math.max(2, Math.min(100, pct))}%` }}
      />
    </div>
  )
}

// Small badge shown next to a phase-row item when the ingest recorded
// soft errors (e.g. comments whose ticket was already gone from
// Zendesk; participants we couldn't resolve to an Appuser or
// CompanyUser). Click → opens a Dialog that lazily fetches the full
// per-aspect error breakdown from the controller's `run_errors`
// endpoint and renders the IDs grouped by kind.
function PhaseItemErrorsBadge({
  count,
  aspectKind,
  label,
  connectionId,
  runId,
}: {
  count: number
  aspectKind?: string
  label: string
  connectionId?: number
  runId?: number
}) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<Record<string, PhaseErrorPayload> | null>(null)
  const canFetch = connectionId != null && runId != null && aspectKind != null

  const fetchErrors = async () => {
    if (!canFetch) return
    setLoading(true)
    try {
      const res = await fetch(`/support_connections/${connectionId}/runs/${runId}/errors/${aspectKind}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!res.ok) {
        toast.error("Could not load error details.")
        setData({})
        return
      }
      const j = await res.json()
      setData(j.errors ?? {})
    } catch (e) {
      toast.error("Could not load error details.")
      setData({})
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); if (data == null) fetchErrors() }}
        className="inline-flex flex-none items-center gap-1 rounded-control border border-status-warning/40 px-1.5 py-0.5 text-[11px] font-medium text-status-warning hover:bg-status-warning/10 disabled:opacity-50"
        disabled={!canFetch}
        title={canFetch ? "Click for details" : "Details unavailable"}
      >
        {fmtNumber(count)} {count === 1 ? "error" : "errors"}
        <Info size={11} aria-hidden />
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} size="xl">
        <DialogHeader title={`${label} — ${fmtNumber(count)} ${count === 1 ? "error" : "errors"}`} />
        <DialogBody>
          {loading && <p className="text-sm text-app-muted">Loading…</p>}
          {!loading && data != null && Object.keys(data).length === 0 && (
            <p className="text-sm text-app-muted">No detail recorded for this resource.</p>
          )}
          {!loading && data != null && Object.entries(data).map(([kind, payload]) => (
            <PhaseErrorsKindBlock
              key={kind}
              kind={kind}
              payload={payload}
              connectionId={connectionId}
              runId={runId}
              aspectKind={aspectKind}
            />
          ))}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Close</Button>
        </DialogFooter>
      </Dialog>
    </>
  )
}

function phaseErrorItems(phases: JobPhase[] | undefined): PhaseErrorItem[] {
  const byAspect = new Map<string, PhaseErrorItem>()
  for (const phase of phases ?? []) {
    for (const item of phase.items ?? []) {
      const count = item.errors_count ?? 0
      const aspectKind = item.aspect_kind
      if (count <= 0 || !aspectKind) continue

      const existing = byAspect.get(aspectKind)
      if (existing) {
        existing.count += count
      } else {
        byAspect.set(aspectKind, { label: item.label, count, aspectKind })
      }
    }
  }
  return Array.from(byAspect.values())
}

// Renders one error kind ("skipped_no_author" / "skipped_no_convo")
// inside the (i) modal, grouped by per-entry resolution outcome.
// Operators see at a glance: how many recovered, how many marked
// system, how many still need attention.
function PhaseErrorsKindBlock({
  kind,
  payload,
  connectionId,
  runId,
  aspectKind,
}: {
  kind:    string
  payload: PhaseErrorPayload
  connectionId?: number
  runId?: number
  aspectKind?: string
}) {
  const entries: PhaseErrorEntry[] = Array.isArray(payload.entries) && payload.entries.length > 0
    ? payload.entries
    : Array.isArray(payload.ticket_ids)
      ? payload.ticket_ids.map((ticketId): PhaseErrorEntry => ({ ticket_id: ticketId }))
      : []
  const sampledCount = payload.sample_count ?? entries.length
  const unresolvedCount = payload.count ?? 0

  // Bucket entries by resolution. Anything without a resolution =
  // "unresolved" (the reconciler hasn't run on it yet, or it was
  // skipped). "unrecoverable" stays its own bucket so the operator
  // can act on it.
  const buckets: Record<string, PhaseErrorEntry[]> = {
    recovered:     [],
    system:        [],
    unrecoverable: [],
    unresolved:    [],
  }
  entries.forEach((e) => {
    const r = (e.resolution ?? "").toString()
    if (r === "recovered" || r === "system" || r === "unrecoverable") buckets[r].push(e)
    else buckets.unresolved.push(e)
  })

  const tone: Record<string, string> = {
    recovered:     "text-status-success",
    system:        "text-app-muted",
    unrecoverable: "text-status-error",
    unresolved:    "text-status-warning",
  }
  const label: Record<string, string> = {
    recovered:     "Recovered",
    system:        "Marked system (Zendesk-side)",
    unrecoverable: "Unrecoverable",
    unresolved:    "Not yet resolved",
  }

  return (
    <div className="mb-5">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-app-text">
        {kind} ({fmtNumber(unresolvedCount)} unresolved · {fmtNumber(sampledCount)} sampled)
      </div>
      {(payload.explanation || defaultErrorExplanation(kind)) && (
        <p className="mb-3 max-w-3xl text-xs leading-relaxed text-app-muted">
          {payload.explanation || defaultErrorExplanation(kind)}
        </p>
      )}
      {payload.pagination && payload.pagination.total_entries > entries.length && (
        <p className="mb-3 text-xs text-app-muted">
          Showing first {fmtNumber(entries.length)} of {fmtNumber(payload.pagination.total_entries)} entries.
        </p>
      )}

      {(["recovered", "system", "unrecoverable", "unresolved"] as const).map((bucketKey) => {
        const list = buckets[bucketKey]
        if (list.length === 0) return null
        return (
          <details key={bucketKey} className="mb-2 rounded-control border border-border bg-white/[0.02] open:bg-white/[0.04]">
            <summary className={cn("cursor-pointer px-3 py-1.5 text-xs font-medium", tone[bucketKey])}>
              {label[bucketKey]} · {fmtNumber(list.length)}
            </summary>
            <PaginatedErrorEntries
              kind={kind}
              entries={list}
              connectionId={connectionId}
              runId={runId}
              aspectKind={aspectKind}
            />
          </details>
        )
      })}
    </div>
  )
}

const ERROR_MODAL_PAGE_SIZE = 100

function PaginatedErrorEntries({
  kind,
  entries,
  connectionId,
  runId,
  aspectKind,
}: {
  kind: string
  entries: PhaseErrorEntry[]
  connectionId?: number
  runId?: number
  aspectKind?: string
}) {
  const [page, setPage] = useState(0)
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [loadingKey, setLoadingKey] = useState<string | null>(null)
  const [detailsByKey, setDetailsByKey] = useState<Record<string, Partial<PhaseErrorEntry>>>({})
  const pageCount = Math.max(1, Math.ceil(entries.length / ERROR_MODAL_PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const start = safePage * ERROR_MODAL_PAGE_SIZE
  const visible = entries.slice(start, start + ERROR_MODAL_PAGE_SIZE)

  const fetchEntryDetail = async (entry: PhaseErrorEntry, key: string) => {
    if (connectionId == null || runId == null || !aspectKind || entry.page_id == null || detailsByKey[key] != null) return
    setLoadingKey(key)
    try {
      const params = new URLSearchParams({
        page_id: String(entry.page_id),
        ticket_id: String(entry.ticket_id ?? ""),
      })
      if (entry.message_id != null) params.set("message_id", String(entry.message_id))
      const res = await fetch(`/support_connections/${connectionId}/runs/${runId}/errors/${aspectKind}/message?${params.toString()}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      const json = await res.json()
      if (!res.ok || !json.ok) {
        setDetailsByKey((prev) => ({ ...prev, [key]: { resolution_detail: json.error || "Message detail unavailable." } }))
        return
      }
      setDetailsByKey((prev) => ({ ...prev, [key]: json.detail ?? {} }))
    } catch {
      setDetailsByKey((prev) => ({ ...prev, [key]: { resolution_detail: "Message detail unavailable." } }))
    } finally {
      setLoadingKey(null)
    }
  }

  const toggleEntry = (entry: PhaseErrorEntry, key: string) => {
    if (expandedKey === key) {
      setExpandedKey(null)
      return
    }
    setExpandedKey(key)
    if (!fullMessageForEntry({ ...entry, ...detailsByKey[key] }) && entry.page_id != null) {
      fetchEntryDetail(entry, key)
    }
  }

  return (
    <div className="px-3 py-2">
      <ul className="space-y-1.5">
        {visible.map((entry, idx) => {
          const key = keyForErrorEntry(entry, start + idx)
          const enrichedEntry = { ...entry, ...detailsByKey[key] }
          const body = fullMessageForEntry(enrichedEntry)
          const isExpanded = expandedKey === key
          return (
            <li key={key} className="rounded-control border border-border bg-black/10">
              <button
                type="button"
                onClick={() => toggleEntry(entry, key)}
                className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-3 px-3 py-2 text-left hover:bg-white/[0.03]"
              >
                <span className="min-w-0">
                  <span className="block truncate text-xs font-medium text-app-text">
                    {entrySubject(enrichedEntry)}
                  </span>
                  <span className="mt-0.5 block truncate font-mono text-[11px] text-app-muted">
                    {idForEntry(enrichedEntry)}
                  </span>
                  {messagePreview(enrichedEntry) && (
                    <span className="mt-1 block truncate text-[11px] text-app-muted/80">
                      {messagePreview(enrichedEntry)}
                    </span>
                  )}
                </span>
                <span className="flex flex-col items-end gap-1 text-[11px] text-app-muted">
                  {entryTime(enrichedEntry) && <span>{fmtAbsoluteShort(entryTime(enrichedEntry) || "")}</span>}
                  {enrichedEntry.zendesk_url && (
                    <span className="text-status-warning">
                      Zendesk
                    </span>
                  )}
                </span>
              </button>
              {isExpanded && (
                <div className="border-t border-border px-3 py-3">
                  <dl className="mb-3 grid gap-x-4 gap-y-1 text-[11px] text-app-muted sm:grid-cols-2">
                    <ErrorEntryMeta label="Ticket" value={enrichedEntry.ticket_id} />
                    <ErrorEntryMeta label={kind === "skipped_no_convo" ? "Sample msg" : "Message"} value={enrichedEntry.message_id ?? enrichedEntry.sample_message_id} />
                    <ErrorEntryMeta label="Author" value={enrichedEntry.ext_id ?? enrichedEntry.author_name} />
                    <ErrorEntryMeta label="Resolution" value={enrichedEntry.resolution_detail ?? enrichedEntry.resolution} />
                    <ErrorEntryMeta label="Page" value={enrichedEntry.page_seq ?? enrichedEntry.page_id} />
                  </dl>
                  {enrichedEntry.zendesk_url && (
                    <a
                      href={String(enrichedEntry.zendesk_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mb-3 inline-flex text-xs font-medium text-status-warning hover:underline"
                    >
                      Open ticket in Zendesk
                    </a>
                  )}
                  {loadingKey === key ? (
                    <p className="text-xs text-app-muted">Loading message from one raw bundle…</p>
                  ) : body ? (
                    <div className="rounded-control border border-border bg-black/20 px-3 py-2 text-sm">
                      <MessageMarkdown content={body} neutralizeLinks />
                    </div>
                  ) : (
                    <p className="text-xs text-app-muted">
                      {enrichedEntry.page_id == null
                        ? "This run does not have a page pointer for lazy message lookup. Rerun Phase 2 to capture row-level detail without scanning raw bundles."
                        : "Raw message text was not available for this row."}
                    </p>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
      {pageCount > 1 && (
        <div className="mt-3 flex items-center justify-between gap-2 text-xs text-app-muted">
          <span>
            Showing {fmtNumber(start + 1)}-{fmtNumber(Math.min(start + ERROR_MODAL_PAGE_SIZE, entries.length))} of {fmtNumber(entries.length)}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setPage(Math.max(0, safePage - 1))} disabled={safePage === 0}>
              Previous
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))} disabled={safePage >= pageCount - 1}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function ErrorEntryMeta({ label, value }: { label: string; value: unknown }) {
  if (value == null || String(value).trim() === "") return null
  return (
    <div className="min-w-0">
      <dt className="font-medium text-app-muted/70">{label}</dt>
      <dd className="truncate font-mono text-app-text">{String(value)}</dd>
    </div>
  )
}

function defaultErrorExplanation(kind: string): string | null {
  if (kind === "skipped_no_author") {
    return "The ticket was imported, but this comment's author id did not match an imported end-user or staff user, so the message was held out instead of being attached to the wrong person."
  }
  if (kind === "skipped_no_convo") {
    return "The comment stream referenced a ticket id that was not imported as a conversation. The safest action is to skip the comment until the parent ticket can be recovered."
  }
  return null
}

// Pull the most operator-meaningful identifier off an error entry.
// skipped_no_author → ext_id (+ ticket/message context); skipped_no_convo
// → ticket_id. Falls back to a comma-joined dump if the shape is
// unfamiliar (forward-compat with new error kinds).
function idForEntry(e: PhaseErrorEntry): string {
  if (e.ext_id != null)    return `ext_id=${e.ext_id} ticket=${e.ticket_id ?? "?"} msg=${e.message_id ?? "?"}`
  if (e.ticket_id != null) return `ticket=${e.ticket_id}`
  return Object.entries(e)
    .filter(([k]) => !["resolution", "resolution_detail", "resolved_at", "message_body", "sample_message_body", "subject", "zendesk_url"].includes(k))
    .map(([k, v]) => `${k}=${v}`)
    .join(" · ")
}

function keyForErrorEntry(e: PhaseErrorEntry, idx: number): string {
  return [
    e.ticket_id ?? "ticket",
    e.message_id ?? e.sample_message_id ?? "message",
    e.ext_id ?? "actor",
    idx,
  ].map((part) => String(part)).join(":")
}

function entrySubject(e: PhaseErrorEntry): string {
  const subject = e.subject != null ? String(e.subject).trim() : ""
  return subject || "Subject unavailable"
}

function fullMessageForEntry(e: PhaseErrorEntry): string {
  return String(e.message_body ?? e.sample_message_body ?? "").trim()
}

function messagePreview(e: PhaseErrorEntry): string {
  const body = fullMessageForEntry(e)
  if (body) return body.replace(/\s+/g, " ").slice(0, 180)
  const detail = e.resolution_detail != null ? String(e.resolution_detail).trim() : ""
  return detail
}

function entryTime(e: PhaseErrorEntry): string | null {
  const value = e.message_created_at ?? e.sample_message_created_at
  return value != null && String(value).trim() !== "" ? String(value) : null
}


// Phase 2 sub-item rendered inside the phase strip while the auto-
// reconciler is running. Shows processed/total + per-outcome counts
// (recovered tickets, recovered authors, marked-as-system,
// unrecoverable). Backed by `job.metadata['reconciler_progress']`,
// written every ~25 items by SupportImports::ReconcileIngestErrors.
function ReconcilerProgressItem({ progress }: { progress: ReconcilerProgress }) {
  const isDone   = progress.phase === "complete" || progress.finished_at != null
  const isErr    = progress.phase === "errored" || progress.error != null
  const total    = Math.max(progress.total ?? 0, 0)
  const done     = Math.min(progress.processed ?? 0, total > 0 ? total : Number.MAX_SAFE_INTEGER)
  const pct      = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0
  const fillCls  = isErr ? "bg-status-error" : isDone ? "bg-status-success" : "bg-status-warning"
  const headline = isErr
    ? "Auto-recovery errored"
    : isDone
      ? "Auto-recovery complete"
      : "Auto-recovering missing data"

  const successCount = (progress.recovered_tickets ?? 0) +
                       (progress.recovered_authors ?? 0) +
                       (progress.marked_system ?? 0)
  const failureCount = (progress.unrecoverable_tickets ?? 0) +
                       (progress.unrecoverable_authors ?? 0)

  return (
    <li className="space-y-1 tabular-nums">
      <div className="flex items-center gap-2">
        <span className="flex-1 text-app-text">{headline}</span>
        <span className="tabular-nums text-app-muted">
          {fmtNumber(done)}/{fmtNumber(total)} done · {pct}%
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/[0.06]">
        <div
          className={cn("h-full rounded-full transition-all duration-500", fillCls)}
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] tabular-nums">
        <span className="text-status-success">
          ✓ {fmtNumber(successCount)} success
        </span>
        <span className="text-status-error">
          ✗ {fmtNumber(failureCount)} failure
        </span>
        <span className="text-app-muted">·</span>
        <span className="text-app-muted">
          {fmtNumber(progress.recovered_tickets ?? 0)} convos recovered
        </span>
        <span className="text-app-muted">
          {fmtNumber(progress.recovered_authors ?? 0)} users recovered
        </span>
        <span className="text-app-muted">
          {fmtNumber(progress.marked_system ?? 0)} marked system
        </span>
      </div>
      {isErr && progress.error && (
        <div className="mt-2 text-[12px] text-status-error">{progress.error}</div>
      )}
    </li>
  )
}

function IngestVerificationErrorsButton({
  items,
  connectionId,
  runId,
}: {
  items: PhaseErrorItem[]
  connectionId: number
  runId: number
}) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<Array<PhaseErrorItem & {
    errors: Record<string, PhaseErrorPayload>
  }> | null>(null)

  const total = items.reduce((sum, item) => sum + item.count, 0)

  const fetchAll = async () => {
    setLoading(true)
    try {
      const next = await Promise.all(items.map(async (item) => {
        const res = await fetch(`/support_connections/${connectionId}/runs/${runId}/errors/${item.aspectKind}`, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        })
        if (!res.ok) return { ...item, errors: {} }
        const json = await res.json()
        return { ...item, errors: json.errors ?? {} }
      }))
      setRows(next)
    } catch {
      toast.error("Could not load error details.")
      setRows(items.map((item) => ({ ...item, errors: {} })))
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); if (rows == null) fetchAll() }}
        className="inline-flex items-center gap-1 rounded-control border border-status-warning/40 px-1.5 py-0.5 text-[11px] font-medium text-status-warning hover:bg-status-warning/10"
      >
        View {fmtNumber(total)} soft-error details
        <Info size={11} aria-hidden />
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} size="xl">
        <DialogHeader title={`Phase 2 soft errors — ${fmtNumber(total)}`} />
        <DialogBody>
          {loading && <p className="text-sm text-app-muted">Loading…</p>}
          {!loading && rows != null && rows.length === 0 && (
            <p className="text-sm text-app-muted">No detail recorded for these errors.</p>
          )}
          {!loading && rows != null && rows.map((row) => (
            <div key={row.aspectKind} className="mb-6 last:mb-0">
              <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                <h4 className="text-sm font-semibold text-app-text">{row.label}</h4>
                <span className="text-xs text-app-muted">{fmtNumber(row.count)} recorded</span>
              </div>
              {Object.keys(row.errors).length === 0 ? (
                <p className="text-sm text-app-muted">No detail recorded for this resource.</p>
              ) : (
                Object.entries(row.errors).map(([kind, payload]) => (
                  <PhaseErrorsKindBlock
                    key={`${row.aspectKind}-${kind}`}
                    kind={kind}
                    payload={payload}
                    connectionId={connectionId}
                    runId={runId}
                    aspectKind={row.aspectKind}
                  />
                ))
              )}
            </div>
          ))}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Close</Button>
        </DialogFooter>
      </Dialog>
    </>
  )
}

function IngestVerificationBlock({
  report,
  phases,
  connectionId,
  runId,
}: {
  report: IngestVerification
  phases?: JobPhase[]
  connectionId?: number
  runId?: number
}) {
  const totals = report.totals ?? {}
  const issues = [...(report.blocking_issues ?? []), ...(report.warnings ?? [])]
  const errorItems = phaseErrorItems(phases)
  const canShowErrorModal = connectionId != null && runId != null && errorItems.length > 0
  const canRerun = connectionId != null && runId != null
  const [rerunBusy, setRerunBusy] = useState(false)
  const rerunVerification = () => {
    if (!canRerun || rerunBusy) return
    setRerunBusy(true)
    router.post(`/support_connections/${connectionId}/runs/${runId}/rerun_ingest_verification`, {}, {
      preserveScroll: true,
      onSuccess: () => toast.success("Ingest verification refreshed"),
      onError: () => toast.error("Could not rerun verification"),
      onFinish: () => setRerunBusy(false),
    })
  }
  const tone = report.ok ? "border-status-success/30 bg-status-success/5" : "border-status-warning/35 bg-status-warning/5"
  const icon = report.ok
    ? <CheckCircle2 size={14} className="text-status-success" />
    : <AlertTriangle size={14} className="text-status-warning" />
  return (
    <div className={cn("mt-3 rounded-control border px-4 py-3", tone)}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="inline-flex items-center gap-2 font-semibold text-app-text">
          {icon}
          Ingest verification {report.ok ? "passed" : "needs review"}
        </span>
        <span className="inline-flex items-center gap-2">
          {report.checked_at && (
            <span className="text-xs text-app-muted">{fmtAbsoluteShort(report.checked_at)}</span>
          )}
          {canRerun && (
            <button
              type="button"
              onClick={rerunVerification}
              disabled={rerunBusy}
              className="inline-flex items-center gap-1 rounded-control border border-border px-1.5 py-0.5 text-[11px] text-app-muted hover:bg-white/5 hover:text-app-text disabled:opacity-50"
            >
              <RefreshCw size={11} className={rerunBusy ? "animate-spin" : ""} />
              Re-run
            </button>
          )}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] tabular-nums text-app-muted">
        <span>{fmtNumber(totals["pages_ingested"] ?? 0)} / {fmtNumber(totals["pages_total"] ?? 0)} pages ingested</span>
        <span>{fmtNumber(totals["ingested_records"] ?? 0)} records written</span>
        <span>{fmtNumber(totals["soft_errors"] ?? 0)} soft errors</span>
        <span>{fmtNumber(totals["failed_records"] ?? 0)} failed records</span>
        {canShowErrorModal && (
          <IngestVerificationErrorsButton
            items={errorItems}
            connectionId={connectionId}
            runId={runId}
          />
        )}
      </div>
      {issues.length > 0 && (
        <ul className="mt-2 space-y-1 text-[12px]">
          {issues.slice(0, 5).map((issue, idx) => (
            <li key={`${issue.kind}-${idx}`} className={issue.severity === "blocking" ? "text-status-error" : "text-status-warning"}>
              {issue.aspect_kind ? `${issue.aspect_kind}: ` : ""}{issue.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function PhaseItemDetail({
  item,
  state,
}: {
  item: NonNullable<JobPhase["items"]>[number]
  state: JobPhase["state"]
}) {
  // While running with a decoded cursor: surface count + cursor date
  // window (so the operator sees how far through customer-time we've
  // walked) + percent + ETA. The denominator on the count side is
  // typically a stale kickoff snapshot, so we render counts WITHOUT a
  // total when we have a cursor-percent (the cursor-percent is the
  // honest progress signal).
  if (item.cursor_at && state === "running") {
    const pct = item.cursor_percent ?? 0
    const eta = fmtEta(item.eta_s)
    const today = fmtDateOnly(new Date().toISOString())
    return (
      <>
        {fmtNumber(item.numerator)} {item.unit}
        {" · "}{fmtDateOnly(item.cursor_at)} → {today}
        {" · "}{pct}%
        {eta ? ` · ETA ${eta}` : ""}
      </>
    )
  }
  if (item.denominator === 0) {
    // Unknown total (e.g. Staff — we don't pre-fetch a count). Show raw
    // numerator + 100% once the phase is done so the row visually agrees
    // with the rest of the strip.
    if (state === "completed") {
      return <>{fmtNumber(item.numerator)} {item.unit} · 100%</>
    }
    return <>{fmtNumber(item.numerator)} {item.unit}</>
  }

  // Phase is done: the denominator was a kickoff-time estimate that's
  // now stale (live tenant added rows during the import OR the estimate
  // was high). Drop the ratio — the actual numerator IS the truth.
  if (state === "completed") {
    return <>{fmtNumber(item.numerator)} {item.unit} · 100%</>
  }

  // Numerator outran the estimate while still running. The ratio reads
  // weirdly as "N / smaller · 100%"; collapse to "N unit · 100%" until
  // the phase formally completes.
  if (item.numerator >= item.denominator) {
    return <>{fmtNumber(item.numerator)} {item.unit} · 100%</>
  }

  const pct = item.percent ?? 0
  const eta = state === "running" ? fmtEta(item.eta_s) : ""
  return (
    <>
      {fmtNumber(item.numerator)} / {fmtNumber(item.denominator)} {item.unit} · {pct}%
      {eta ? ` · ETA ${eta}` : ""}
    </>
  )
}

// Date-only formatter for cursor-progress pills, e.g. "May 3 2025".
// `fmtAbsoluteShort` includes time-of-day which is too noisy for the
// cursor's "we've loaded through this date" signal.
function fmtDateOnly(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (isNaN(d.getTime())) return "—"
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
}

// Render the operator's appuser-source-key-field choice in human form.
// Stored values come from `Zendesk#list_user_id_attribute_options`:
//   "external_id"           → "external_id"
//   "user_field:<key>"      → "<key> (custom field)"
// Unknown shapes pass through verbatim so we don't hide config drift.
function fmtSourceKeyField(value: string): string {
  if (value === "external_id") return "external_id"
  if (value.startsWith("user_field:")) return `${value.slice("user_field:".length)} (custom field)`
  return value
}

// Provider display name in our chip-friendly camel case. Always
// "ZenDesk" for zendesk regardless of case-by-case identity strings.
function providerChipName(provider: string): string {
  if (provider === "zendesk") return "ZenDesk"
  return provider.charAt(0).toUpperCase() + provider.slice(1)
}

// Decompose `appuser_source_key_field` into chip parts:
//   { kind: "BuiltIn",    value: "external_id" }   ← stock Zendesk attr
//   { kind: "CustomField", value: "appuser_uuid" }  ← user_fields.<key>
// The set of "built-in" keys is the stock attributes Zendesk surfaces
// outside `user_fields` (right now we only use external_id; future:
// email, phone, name).
const SOURCE_KEY_BUILTINS = new Set([ "external_id", "email", "phone", "name" ])
function decomposeSourceKey(value: string): { kind: "BuiltIn" | "CustomField"; value: string } {
  if (value.startsWith("user_field:")) {
    return { kind: "CustomField", value: value.slice("user_field:".length) }
  }
  if (SOURCE_KEY_BUILTINS.has(value)) return { kind: "BuiltIn", value }
  // Unknown shape — render as a custom field so we don't hide drift.
  return { kind: "CustomField", value }
}

function PhaseStateIcon({ state }: { state: JobPhase["state"] }) {
  switch (state) {
    case "completed":
      return <CheckCircle2 size={14} className="flex-none text-status-success" aria-label="completed" />
    case "running":
      return <RefreshCw size={14} className="flex-none animate-spin text-status-warning" aria-label="running" />
    case "failed":
      return <XCircle size={14} className="flex-none text-status-error" aria-label="failed" />
    case "cancelled":
      return <XCircle size={14} className="flex-none text-status-error" aria-label="cancelled" />
    case "skipped":
      return <Circle size={14} className="flex-none text-app-muted" aria-label="skipped" />
    case "pending":
    default:
      return <Circle size={14} className="flex-none text-app-muted/50" aria-label="pending" />
  }
}

function PhaseRightDetail({ phase }: { phase: JobPhase }) {
  switch (phase.state) {
    case "completed":
      return <>{fmtDuration(phase.started_at, phase.completed_at)}</>
    case "running": {
      const num = phase.numerator ?? 0
      const den = phase.denominator ?? 0
      const unit = phase.unit ?? ""
      if (den === 0) {
        return <>{fmtNumber(num)}{unit ? ` ${unit}` : ""} · in progress</>
      }
      const pct  = phase.percent ?? 0
      const eta  = fmtEta(phase.eta_s)
      return (
        <>
          {fmtNumber(num)} / {fmtNumber(den)}{unit ? ` ${unit}` : ""} · {pct}%
          {eta ? ` · ETA ${eta}` : ""}
        </>
      )
    }
    case "pending":
      return <span className="text-app-muted">pending</span>
    case "skipped":
      return <span className="text-app-muted">skipped</span>
    case "failed":
      return (
        <span className="text-status-error">
          failed{phase.started_at ? ` · ${fmtDuration(phase.started_at, phase.completed_at)}` : ""}
        </span>
      )
    case "cancelled":
      return <span className="text-status-error">cancelled</span>
    default:
      return null
  }
}

function ResourceChecklistRow({ resource }: { resource: ResourceStatus }) {
  const label    = RESOURCE_LABELS[resource.resource_kind] ?? resource.resource_kind
  const dlState  = resource.download_state
  const impState = resource.import_state
  const count    = resource.count_imported

  // The numeric count lives in its own slot before the icons (right-aligned,
  // tabular-nums so digits stay vertically aligned across rows). Trailing
  // detail covers the qualitative states only — when there's a count it
  // tells the operator what's happening with that count.
  let detail: string | null = null
  if (dlState === "failed" || impState === "failed") {
    detail = resource.error_message ? "fetch failed" : "failed"
  } else if (dlState === "skipped" || impState === "skipped") {
    detail = resource.error_message ?? "not available"
  } else if (dlState === "running" || impState === "running") {
    detail = count > 0 ? "downloading…" : "starting…"
  } else if (count === 0) {
    detail = "not started yet"
  }

  const rowTooltip = resource.error_message ?? undefined

  return (
    <li className="flex items-center gap-2" title={rowTooltip}>
      <span className="flex-1 text-app-text">{label}</span>
      <span className="flex-none w-16 text-right text-xs tabular-nums text-app-muted">
        {count > 0 ? fmtNumber(count) : ""}
      </span>
      <span className="flex-none flex items-center gap-1">
        <span title={phaseTooltip("Downloaded", dlState)} className="flex-none leading-none">
          <Download
            size={13}
            className={phaseIconClass(dlState)}
            aria-label={phaseTooltip("Downloaded", dlState)}
          />
        </span>
        <span title={phaseTooltip("Imported", impState)} className="flex-none leading-none">
          <Database
            size={13}
            className={phaseIconClass(impState)}
            aria-label={phaseTooltip("Imported", impState)}
          />
        </span>
      </span>
      {detail !== null && <span className="text-xs text-app-muted">{detail}</span>}
    </li>
  )
}

// Operator-set ingestion rate (req/min). Persisted to
// `connection.config.ingest_rate_per_min`. Picked up by in-flight
// FetchPages workers within ~one page interval — RateBucket re-reads on
// every take(), no instance memo. Account-wide cap and adapter default
// flow in via the connection payload so the helper text stays accurate.
// Webhook status panel — shown on the Monitor card for active Zendesk
// connections. Lets the operator toggle event-sync on/off, see when each
// of the two child webhooks last received a delivery, re-register if
// the customer-side state has drifted, and run Zendesk's own webhook
// /test endpoint as a smoke check.
function WebhookStatusBlock({ connection }: { connection: ExistingConnection }) {
  const onboardingDone = !!connection.onboarding_completed_at
  const syncOn         = !!connection.sync_events_to_valet
  const webhooks       = connection.webhooks ?? []
  const ticketsRow     = webhooks.find((w) => w.kind === "tickets")
  const eventsRow      = webhooks.find((w) => w.kind === "events")

  const staleAfterMs   = 24 * 60 * 60 * 1000
  const now            = Date.now()
  const webhookStale   = (w: ConnectionWebhook | undefined) =>
    w?.state === "active" && w.last_received_at != null && (now - new Date(w.last_received_at).getTime()) > staleAfterMs
  const webhookHealthy = (w: ConnectionWebhook | undefined) =>
    w != null && w.state === "active" && !webhookStale(w)
  const notSyncing     = !onboardingDone && (!syncOn || !webhookHealthy(ticketsRow) || !webhookHealthy(eventsRow))

  const post = (action: string, body: InertiaFormObject = {}, success?: string) => {
    router.post(`/support_connections/${connection.id}/${action}`, body, {
      onSuccess: () => { if (success) toast.success(success) },
      onError: (errors) => {
        const first = Object.values(errors)[0] as string | string[] | undefined
        const msg = Array.isArray(first) ? first[0] : first
        toast.error(msg ?? "Action failed")
      },
    })
  }

  return (
    <div className="border-t border-border px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-app-text">Webhook sync</div>
            {notSyncing && (
              <AlertTriangle size={14} className="text-status-warning" aria-label="Webhooks not syncing" />
            )}
          </div>
          <div className="mt-0.5 text-xs text-app-muted">
            Real-time mirror of Zendesk activity into Valet. Polling stays on as a
            safety net — webhooks add immediacy.
          </div>
        </div>
        <label
          className={`flex cursor-pointer items-center gap-2 text-sm ${onboardingDone ? "cursor-not-allowed text-app-muted" : "text-app-text"}`}
        >
          <input
            type="checkbox"
            checked={syncOn}
            disabled={onboardingDone}
            onChange={(e) =>
              post("toggle_sync", { enabled: e.target.checked }, e.target.checked ? "Webhooks registered" : "Webhooks deregistered")
            }
          />
          {onboardingDone ? "Onboarding complete — sync ended" : (syncOn ? "Sync events to Valet" : "Sync paused")}
        </label>
      </div>

      {notSyncing && (
        <div className="mt-3 flex items-start gap-2 rounded-control border border-status-warning/40 bg-status-warning/10 px-3 py-2 text-sm text-app-text">
          <AlertTriangle size={16} className="mt-0.5 flex-none text-status-warning" aria-hidden />
          <p className="flex-1">
            We are not syncing your data. Make sure to set up the webhooks to stay up
            to date until switchover is complete.
          </p>
        </div>
      )}

      {!onboardingDone && syncOn && (
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <WebhookRowDisplay row={ticketsRow ?? null} kind="tickets" label="Tickets + comments" />
          <WebhookRowDisplay row={eventsRow ?? null} kind="events" label="End-users + KB articles" />
        </div>
      )}

      {!onboardingDone && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => post("re_register_webhook", {}, "Re-registered with Zendesk")}>
            <RefreshCw size={13} className="mr-1.5" />
            Re-register
          </Button>
          <Button variant="ghost" size="sm" onClick={() => post("test_webhook", {}, "Zendesk test event sent")}>
            Send test event
          </Button>
        </div>
      )}
    </div>
  )
}

function WebhookRowDisplay({ row, kind, label }: { row: ConnectionWebhook | null; kind: "tickets" | "events"; label: string }) {
  if (row == null) {
    return (
      <div className="rounded-control border border-border bg-white/[0.02] px-3 py-2 text-sm">
        <div className="text-app-text">{label}</div>
        <div className="mt-0.5 text-[12px] text-app-muted">Not registered yet — hit Re-register.</div>
      </div>
    )
  }

  const tone = row.state === "active" ? "success" : (row.state === "errored" ? "danger" : "warning")
  const last = row.last_received_at ? fmtRelativeTime(row.last_received_at) : "never"

  return (
    <div className="rounded-control border border-border bg-white/[0.02] px-3 py-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="text-app-text">{label}</span>
        <Badge tone={tone} className="px-2 py-0">{row.state}</Badge>
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[12px] text-app-muted">
        <span>last event: {last}</span>
        {kind === "tickets" && <span>· {row.trigger_count} trigger{row.trigger_count === 1 ? "" : "s"}</span>}
        {row.last_error && <span className="text-status-error">· {row.last_error}</span>}
      </div>
    </div>
  )
}

function RateLimitBlock({ connection }: { connection: ExistingConnection }) {
  const fallback = connection.rate_limit_default ?? 500
  const ceiling  = connection.rate_limit_account_cap ?? 10_000
  const initial  = connection.ingest_rate_per_min ?? fallback
  const [value, setValue] = useState<string>(String(initial))
  const [error, setError] = useState<string | null>(null)
  const lastSavedRef      = useRef<number>(initial)

  const commit = () => {
    if (!/^\d+$/.test(value)) {
      setError(`Must be between 1 and ${ceiling}.`)
      return
    }
    const n = Number(value)
    if (n === lastSavedRef.current) return // nothing changed
    if (n < 1 || n > ceiling) {
      setError(`Must be between 1 and ${ceiling}.`)
      return
    }
    setError(null)
    router.patch(
      `/support_connections/${connection.id}/rate_limit`,
      { rate: { ingest_rate_per_min: n } },
      {
        preserveScroll: true,
        onSuccess: () => {
          lastSavedRef.current = n
          toast.success(`Ingestion rate set to ${n} req/min — applies to running jobs.`)
          router.reload({ only: ["existing_connections"] })
        },
        onError: (errs) => {
          const first = (Object.values(errs)[0] as string | string[] | undefined)
          const msg   = Array.isArray(first) ? first[0] : first
          setError(msg ?? "Update failed")
          setValue(String(lastSavedRef.current))
          toast.error(msg ?? "Could not update rate")
        },
      },
    )
  }

  return (
    <div className="border-t border-border px-5 py-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-app-muted">Ingestion rate</div>
        <span className="text-[11px] text-app-muted">
          Account cap {ceiling} req/min · adapter default {fallback}
        </span>
      </div>
      <p className="mb-3 text-xs text-app-muted">
        {connection.provider_display_name}'s account-wide rate limit is shared across <em>all</em> integrations
        on the account. If other tools (apps, widgets, chat, ETL) also call this account, lower this number
        to leave them headroom. Changes apply to in-flight jobs within ~one page interval.
      </p>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={value}
          onChange={(e) => { setValue(e.target.value); setError(null) }}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur() } }}
          inputMode="numeric"
          min={1}
          max={ceiling}
          className="w-32"
        />
        <span className="text-xs text-app-muted">req/min</span>
      </div>
      {error && <p className="mt-2 text-[11px] text-brand-red">{error}</p>}
    </div>
  )
}

// Per-row progress cell shown inside the Recent runs table.
//
// Terminal runs render the final record count, no bar.
// Active runs (`running` / `pending`) render a compact two-line cell:
//   line 1: numerator / estimated_total · pct% (or `N records · running`
//           when there's no known denominator).
//   line 2: a ~80px mini bar (skipped for unknown denominator).
//
function StartPhase3Button({
  connectionId,
  runId,
}: {
  connectionId: number
  runId: number
}) {
  const [busy, setBusy] = useState(false)
  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (busy) return
    setBusy(true)
    router.post(`/support_connections/${connectionId}/runs/${runId}/start_phase3`, {}, {
      onSuccess: () => toast.success("Phase 3 recommendation generation started"),
      onError: () => toast.error("Could not start Phase 3."),
      onFinish: () => setBusy(false),
    })
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center gap-1 rounded-control border border-status-success/40 px-2 py-0.5 text-[11px] font-medium text-status-success hover:bg-status-success/10 disabled:opacity-50"
    >
      <ArrowRight size={11} />
      {busy ? "Starting…" : "Start Phase 3"}
    </button>
  )
}

// We pick the smaller of Phase 1 (download) and Phase 2 (apply) ticket
// counts as the visible numerator — Phase 2 always lags Phase 1, so
// "tickets safely in DB" is the honest progress number for an operator.
function RunProgressCell({ run }: { run: ConnectionRun }) {
  const isActive   = run.state === "running" || run.state === "pending"
  const finalCount = fmtNumber(run.total_records)
  const phases     = run.phases ?? []

  // Phase-state-machine path: the conflated single bar (Phase 1 + Phase 2
  // counts mashed together) is misleading once we have per-phase progress
  // from the JobPhase ladder, so fall through to a current-phase summary.
  if (phases.length > 0) {
    const running = phases.find((p) => p.state === "running")
    if (running) {
      const eta = fmtEta(running.eta_s)
      return (
        <span className="inline-flex flex-col items-end leading-tight">
          <span className="font-mono text-[11px]">
            Phase {running.slot} of {phases.length}: {running.label}
          </span>
          {eta && (
            <span className="text-[10px] uppercase tracking-wide text-app-muted">
              ETA {eta}
            </span>
          )}
        </span>
      )
    }
    // No running phase. If the overall run is still active (pending) just
    // fall through to the legacy display. Otherwise treat all-complete /
    // terminal and surface the elapsed time.
    if (!isActive) {
      return (
        <span className="inline-flex flex-col items-end leading-tight">
          <span className="font-mono text-[11px]">All phases complete</span>
          <span className="text-[10px] uppercase tracking-wide text-app-muted">
            {fmtDuration(run.started_at, run.completed_at)}
          </span>
        </span>
      )
    }
  }

  if (!isActive) return <>{finalCount}</>

  // :comment_fetch — no easy ticket-side estimate from Zendesk. Show the
  // raw count (mirrors the SupportImportAspect.total_records column the
  // adapter increments per page) as N events fetched.
  if (run.kind === "comment_fetch") {
    return (
      <span className="inline-flex flex-col items-end leading-tight">
        <span className="font-mono text-[11px]">{finalCount} events</span>
        <span className="text-[10px] uppercase tracking-wide text-app-muted">running</span>
      </span>
    )
  }

  const estimated = run.estimated_records ?? 0
  // Numerator = Phase 1 ticket fetch count (run.total_records — bumped per
  // page as the adapter persists raw bundles). Phase 2 (DB ingest) lags
  // Phase 1 by an indeterminate amount; gating the bar on Phase 2 leaves
  // it at 0 for the entire fetch window, which reads as "nothing's
  // happening." Operators want fetch progress visible now; the bar
  // settles to the true Phase 2 count after the run terminates.
  const numerator = run.total_records

  if (estimated <= 0) {
  // No known denominator (sync_tick / audit_sample / raw-bundle replay).
    return (
      <span className="inline-flex flex-col items-end leading-tight">
        <span className="font-mono text-[11px]">{fmtNumber(numerator)} records</span>
        <span className="text-[10px] uppercase tracking-wide text-app-muted">running</span>
      </span>
    )
  }

  const pct = Math.min(100, Math.max(0, Math.round((numerator / estimated) * 100)))

  return (
    <span className="inline-flex flex-col items-end gap-0.5 leading-tight">
      <span className="font-mono text-[11px]">
        {fmtNumber(numerator)} / {fmtNumber(estimated)} · {pct}%
      </span>
      <span className="block h-1 w-20 overflow-hidden rounded-full border border-border bg-white/5">
        <span className="block h-full bg-brand-red transition-all" style={{ width: `${pct}%` }} />
      </span>
    </span>
  )
}

function fmtAbsoluteShort(iso: string | null): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (isNaN(d.getTime())) return "—"
  // e.g. "May 2, 2:34 PM"
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
}

function fmtDuration(startIso: string | null, endIso: string | null): string {
  if (!startIso) return "—"
  // Running rows pass endIso=null. Elapsed-since-start is the useful value
  // — the state pill in the same row already conveys "running", so the
  // duration cell stays numeric. Refreshes on each Inertia poll.
  const start = new Date(startIso).getTime()
  const end   = endIso ? new Date(endIso).getTime() : Date.now()
  if (isNaN(start) || isNaN(end) || end < start) return "—"
  let secs = Math.round((end - start) / 1000)
  if (secs < 60) return `${secs}s`
  const mins = Math.floor(secs / 60)
  secs = secs % 60
  if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
  const hours = Math.floor(mins / 60)
  const remMins = mins % 60
  return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`
}

// ETA formatter for JobPhase rows. Returns "" for null/0/negative so the
// caller can omit the "ETA …" suffix entirely without a special case.
function fmtEta(seconds: number | null | undefined): string {
  if (seconds == null) return ""
  if (seconds <= 0) return ""
  if (seconds < 60)   return `${seconds}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  const hours = Math.floor(seconds / 3600)
  const mins  = Math.round((seconds % 3600) / 60)
  return `${hours}h ${mins}m`
}

function RunsTable({
  runs,
  providerDisplayName,
  connectionId,
  canEdit,
}: {
  runs: ConnectionRun[]
  providerDisplayName: string
  connectionId: number
  canEdit: boolean
}) {
  // Auto-expand the most recent run on first render so the operator sees
  // its per-resource breakdown without an extra click. Subsequent toggles
  // (collapse / expand other rows) are user-driven and persist while the
  // page is mounted.
  const initial = useMemo<Set<number>>(() => {
    const s = new Set<number>()
    if (runs.length > 0) s.add(runs[0].id)
    return s
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const [expanded, setExpanded] = useState<Set<number>>(initial)
  const [stopBusyId, setStopBusyId] = useState<number | null>(null)
  const [confirmStopId, setConfirmStopId] = useState<number | null>(null)
  const [runActionBusy, setRunActionBusy] = useState<string | null>(null)

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const stopRun = async (runId: number) => {
    setStopBusyId(runId)
    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
      const res = await fetch(`/support_connections/${connectionId}/runs/${runId}/cancel`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
      })
      if (res.ok || res.redirected || res.status < 400) {
        toast.success(`Run #${runId} cancelled`)
      } else {
        toast.error("Could not cancel run")
      }
    } catch {
      toast.error("Could not cancel run")
    } finally {
      setStopBusyId(null)
      setConfirmStopId(null)
      router.reload({ only: ["existing_connections"] })
    }
  }

  const postRunAction = async (runId: number, action: "retry" | "retry_reconciliation" | "reset_stuck_pages", success: string) => {
    const key = `${action}:${runId}`
    setRunActionBusy(key)
    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
      const res = await fetch(`/support_connections/${connectionId}/runs/${runId}/${action}`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
      })
      if (res.ok || res.redirected || res.status < 400) toast.success(success)
      else toast.error("Action failed")
    } catch {
      toast.error("Action failed")
    } finally {
      setRunActionBusy(null)
      router.reload({ only: ["existing_connections"] })
    }
  }

  const COL_COUNT = 9

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead className="text-[10px] uppercase tracking-wide text-app-muted">
          <tr>
            <th className="w-6 py-2 pr-1" aria-label="Expand" />
            <th className="py-2 pr-3 text-left">ID</th>
            <th className="py-2 pr-3 text-left">Kind</th>
            <th className="py-2 pr-3 text-left">State</th>
            <th className="py-2 pr-3 text-left">Started at</th>
            <th className="py-2 pr-3 text-left">Duration</th>
            <th className="py-2 pr-3 text-right">Records</th>
            <th className="py-2 pr-3 text-right">Failed</th>
            <th className="py-2 pr-3 text-right" aria-label="Actions" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border/40">
          {runs.map((r) => {
            const isOpen = expanded.has(r.id)
            return (
              <Fragment key={r.id}>
                <tr
                  className="cursor-pointer align-top hover:bg-white/[0.02]"
                  onClick={() => toggle(r.id)}
                >
                  <td className="py-2 pr-1 align-middle">
                    {isOpen
                      ? <ChevronDown size={12} className="text-app-muted" />
                      : <ChevronRight size={12} className="text-app-muted" />}
                  </td>
                  <td className="py-2 pr-3 font-mono tabular-nums text-app-muted">#{r.id}</td>
                  <td className="py-2 pr-3">{r.kind.replace(/_/g, " ")}</td>
                  <td className="py-2 pr-3"><Badge tone={RUN_STATE_TONE[r.state]}>{r.state}</Badge></td>
                  <td className="py-2 pr-3 tabular-nums">{fmtAbsoluteShort(r.started_at)}</td>
                  <td className="py-2 pr-3 tabular-nums">{fmtDuration(r.started_at, r.completed_at)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    <RunProgressCell run={r} />
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {r.failed_records > 0 ? <span className="text-status-error">{fmtNumber(r.failed_records)}</span> : "—"}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {canEdit && (r.state === "running" || r.state === "pending") ? (
                      <div className="flex justify-end gap-1">
                        {r.state === "running" && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              postRunAction(r.id, "reset_stuck_pages", "Stuck pages reset")
                            }}
                            disabled={runActionBusy === `reset_stuck_pages:${r.id}`}
                            className="inline-flex items-center gap-1 rounded-control border border-status-warning/40 px-2 py-0.5 text-[11px] font-medium text-status-warning hover:bg-status-warning/10 disabled:opacity-50"
                          >
                            <RefreshCw size={10} />
                            Reset stuck
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setConfirmStopId(r.id)
                          }}
                          disabled={stopBusyId === r.id}
                          className="inline-flex items-center gap-1 rounded-control border border-status-error/40 px-2 py-0.5 text-[11px] font-medium text-status-error hover:bg-status-error/10 disabled:opacity-50"
                        >
                          <Square size={10} />
                          {stopBusyId === r.id ? "Stopping…" : "Stop"}
                        </button>
                      </div>
                    ) : canEdit && r.state === "partial" ? (
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            postRunAction(r.id, "retry_reconciliation", "Recovery retry queued")
                          }}
                          disabled={runActionBusy === `retry_reconciliation:${r.id}`}
                          className="inline-flex items-center gap-1 rounded-control border border-status-warning/40 px-2 py-0.5 text-[11px] font-medium text-status-warning hover:bg-status-warning/10 disabled:opacity-50"
                        >
                          <RefreshCw size={10} />
                          Retry recovery
                        </button>
                        <StartPhase3Button connectionId={connectionId} runId={r.id} />
                      </div>
                    ) : canEdit && r.state === "failed" ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          postRunAction(r.id, "retry", "Retry queued")
                        }}
                        disabled={runActionBusy === `retry:${r.id}`}
                        className="inline-flex items-center gap-1 rounded-control border border-status-warning/40 px-2 py-0.5 text-[11px] font-medium text-status-warning hover:bg-status-warning/10 disabled:opacity-50"
                      >
                        <RefreshCw size={10} />
                        Retry failed
                      </button>
                    ) : null}
                  </td>
                </tr>
                {isOpen && (
                  <tr className="bg-white/[0.02]">
                    <td colSpan={COL_COUNT} className="px-3 py-3 pl-8">
                      <PhaseStrip
                        phases={r.phases ?? []}
                        connectionId={connectionId}
                        runId={r.id}
                        reconciler={r.reconciler}
                        ingestVerification={r.ingest_verification}
                      />
                      {(r.phases ?? []).length === 0 && (r.resource_statuses ?? []).length > 0 && (
                        <ResourceChecklist
                          resources={r.resource_statuses}
                          providerDisplayName={providerDisplayName}
                        />
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>

      <Dialog open={confirmStopId !== null} onClose={() => setConfirmStopId(null)} size="sm">
        <DialogHeader title={confirmStopId !== null ? `Stop run #${confirmStopId}?` : "Stop run?"} />
        <DialogBody>
          <p className="text-sm text-app-text">
            In-flight downloads will be discarded. Pages already saved will remain — you can re-run a Full Download afterwards to resume.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setConfirmStopId(null)}>Cancel</Button>
          <Button
            onClick={() => { if (confirmStopId !== null) stopRun(confirmStopId) }}
            disabled={stopBusyId !== null}
            className="border border-status-error/50 bg-status-error/15 text-status-error hover:bg-status-error/25"
          >
            Stop run
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  )
}

function CutoverDialog({
  open,
  onClose,
  connection,
  onConfirm,
}: {
  open: boolean
  onClose: () => void
  connection: ExistingConnection
  onConfirm: () => void
}) {
  const [typed, setTyped] = useState("")
  const target = connection.name || connection.key || connection.provider_display_name
  const matches = typed.trim() === target

  useEffect(() => {
    if (!open) setTyped("")
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogHeader title={`Disconnect ${connection.provider_display_name}?`} />
      <DialogBody>
        <div className="space-y-3 text-sm">
          <ul className="space-y-1 text-app-text">
            <li>✓ Imported tickets remain queryable forever</li>
            <li>✓ All operator activity history preserved</li>
            <li>✓ The AI agent and any bootstrap-derived skills/FAQs stay live</li>
          </ul>
          <div className="rounded-control border border-border bg-white/[0.02] p-3 text-xs text-app-muted">
            <div className="font-medium text-app-text">Will be cleaned up:</div>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              <li>Raw S3 bundles purged at +30 days</li>
              <li>Import-lifecycle telemetry GC'd at +90 days</li>
              <li>The connection row stays as audit anchor</li>
            </ul>
          </div>
          <p className="text-xs text-status-error">This action cannot be undone via the portal.</p>
          <div>
            <label className="block text-[11px] uppercase tracking-wide text-app-muted">
              Type <span className="font-mono text-app-text normal-case">{target}</span> to confirm
            </label>
            <Input
              className="mt-1"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              placeholder={target}
            />
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button
          onClick={onConfirm}
          disabled={!matches}
          className="border border-status-error/50 bg-status-error/15 text-status-error hover:bg-status-error/25"
        >
          Disconnect
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

// Live ticket-count, surfaced on the Monitor card. The over-100k
// scale warning + "switch to JSON bulk export" CTA do NOT belong here —
// by the time the operator is on Monitor they've already committed.
// The pre-commit warning lives on the Initialize tab (CountTotalBanner).
function MonitorCountBanner({
  connection,
}: {
  connection: ExistingConnection
}) {
  const total = connection.last_count_total ?? 0
  if (total <= 0) return null

  return (
    <div className="rounded-control border border-border bg-white/[0.03] px-3 py-2 text-sm text-app-text">
      Found {fmtNumber(total)} tickets in this {connection.provider_display_name}.
    </div>
  )
}

// Hard-delete the connection AND its full provenance graph: every
// imported ticket, comment, end-user, raw S3 bundle, run, job, and
// finally the connection row itself. Operator-confirmation pattern
// matches CutoverDialog (type-the-key) so accidental clicks don't
// nuke a multi-day import.
function DeleteConnectionDialog({
  open,
  onClose,
  connection,
  onConfirm,
}: {
  open: boolean
  onClose: () => void
  connection: ExistingConnection
  onConfirm: () => void
}) {
  const [typed, setTyped] = useState("")
  const target  = connection.name || connection.key || connection.provider_display_name
  const matches = typed.trim() === target

  useEffect(() => {
    if (!open) setTyped("")
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogHeader title={`Delete ${connection.provider_display_name}?`} />
      <DialogBody>
        <div className="space-y-3 text-sm">
          <div className="rounded-control border border-status-error/40 bg-status-error/10 p-3 text-app-text">
            <div className="font-medium">This will permanently delete:</div>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
              <li>All imported tickets (agent_convos)</li>
              <li>All imported comments (agent_messages)</li>
              <li>All imported end-users + their email history</li>
              <li>All raw download bundles (S3 storage will be reclaimed)</li>
              <li>The connection record itself</li>
            </ul>
          </div>
          <p className="text-xs text-status-error">
            <strong>This cannot be undone.</strong> If the import is still in progress,
            all in-flight work will be lost.
          </p>
          <p className="text-xs text-app-muted">
            The cascade runs in the background in 100,000-row batches; large connections
            (millions of tickets) may take several minutes to fully drain.
          </p>
          <div>
            <label className="block text-[11px] uppercase tracking-wide text-app-muted">
              Type <span className="font-mono text-app-text normal-case">{target}</span> to confirm
            </label>
            <Input
              className="mt-1"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              placeholder={target}
            />
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button
          onClick={onConfirm}
          disabled={!matches}
          className="border border-status-error/60 bg-status-error/15 text-status-error hover:bg-status-error/25"
        >
          Delete forever
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

function FullDownloadDialog({
  open,
  onClose,
  connection,
  onConfirm,
}: {
  open: boolean
  onClose: () => void
  connection: ExistingConnection
  onConfirm: () => void
}) {
  const provider = connection.provider_display_name
  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogHeader title={`Run a full download of ${provider}?`} />
      <DialogBody>
        <div className="space-y-3 text-sm text-app-text">
          <p>
            This pulls your entire history of tickets, comments, knowledge-base articles,
            and end-users from {provider}. It can take from a few minutes to over an hour
            depending on account size — {provider} caps the rate at ~700 requests per minute.
          </p>
          <p>
            You only need to run this once. After it succeeds, the daily auto-sync handles
            new and updated tickets automatically.
          </p>
          <p className="text-app-muted">Stay on this page to watch progress.</p>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm}>
          <Sparkles size={14} className="mr-1.5" />
          Start full download
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

function OnboardingConfigure({
  run,
  connections,
  agents,
  canEdit,
}: {
  run: SupportImportJob
  connections: Array<{ id: number; name: string | null; key: string; provider_display_name: string; ticket_count?: number }>
  agents: Array<{ id: number; name: string }>
  canEdit: boolean
}) {
  const [busy, setBusy] = useState<string | null>(null)
  const seedDefaults = () => {
    setBusy("seed")
    router.post(`/support_import_proposed_agents/seed_defaults?support_import_job_id=${run.id}`, {}, {
      preserveScroll: true,
      onSuccess: () => toast.success("Default agent group ready"),
      onError: () => toast.error("Could not seed default agent group"),
      onFinish: () => setBusy(null),
    })
  }
  const build = (id: number) => {
    setBusy(`build:${id}`)
    router.post(`/support_import_proposed_agents/${id}/build`, {}, {
      preserveScroll: true,
      onSuccess: () => toast.success("Recommendation build queued"),
      onError: () => toast.error("Could not queue build"),
      onFinish: () => setBusy(null),
    })
  }

  return (
    <div className="space-y-6">
      <Card className="p-4">
        <div className="mb-2 text-xs uppercase tracking-wide text-app-muted">Onboarding progress</div>
        <OnboardingRunPhaseStrip phases={run.phases} />
      </Card>

      <Card className="p-5">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-app-text">Agent recommendation groups</h3>
            <p className="mt-1 text-sm text-app-muted">
              Phase 3 writes proposals here. Applying live records waits for Review & apply.
            </p>
          </div>
          {canEdit && (
            <Button variant="ghost" size="sm" onClick={seedDefaults} disabled={busy === "seed"}>
              <Plus size={14} className="mr-1.5" />
              {busy === "seed" ? "Seeding…" : "Seed default group"}
            </Button>
          )}
        </div>

        <div className="mb-4 grid gap-2 sm:grid-cols-2">
          {connections.map((connection) => (
            <div key={connection.id} className="rounded-control border border-border bg-white/[0.02] px-3 py-2 text-sm">
              <div className="font-medium text-app-text">{connection.name || connection.provider_display_name}</div>
              <div className="mt-0.5 text-[12px] text-app-muted">
                {connection.provider_display_name}
                {connection.ticket_count != null ? ` · ${fmtNumber(connection.ticket_count)} tickets` : ""}
              </div>
            </div>
          ))}
        </div>

        {run.proposed_agents.length === 0 ? (
          <p className="text-sm text-app-muted">No groups yet. Start Phase 3 from Monitor or seed a default group here.</p>
        ) : (
          <ul className="space-y-2">
            {run.proposed_agents
              .slice()
              .sort((a, b) => a.position - b.position)
              .map((group) => (
                <li key={group.id} className="flex flex-wrap items-center justify-between gap-3 rounded-control border border-border px-3 py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-app-text">{group.name}</span>
                      <Badge tone={BOOTSTRAP_STATE_TONE[group.build_state]}>{group.build_state.replace(/_/g, " ")}</Badge>
                    </div>
                    <div className="mt-0.5 text-[12px] text-app-muted">
                      {group.connection_ids.length} connection{group.connection_ids.length === 1 ? "" : "s"}
                      {group.compare_against_agent_id
                        ? ` · augments ${agents.find((a) => a.id === group.compare_against_agent_id)?.name ?? "existing agent"}`
                        : " · net-new"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => router.visit(`/settings/onboarding?tab=review&run_id=${group.id}`)}>
                      Review
                    </Button>
                    {canEdit && group.build_state === "pending" && (
                      <Button size="sm" onClick={() => build(group.id)} disabled={busy === `build:${group.id}`}>
                        <Sparkles size={14} className="mr-1.5" />
                        {busy === `build:${group.id}` ? "Building…" : "Build"}
                      </Button>
                    )}
                  </div>
                </li>
              ))}
          </ul>
        )}
      </Card>
    </div>
  )
}

// ============================================================
// Tab 4 — Review & apply
// ============================================================
//
// Replaces the legacy "Generate" tab. The Generate-form modal is gone —
// bootstrap runs are now spawned automatically by the SupportImportJob
// orchestration when the operator clicks "Build agents" on Tab 3. This
// tab is purely a per-group review surface: the sidebar lists every
// `SupportImportProposedAgent` from the active SupportImportJob, selecting one
// renders that group's `support_import_proposed_agent` review surface inline (or a
// placeholder until the bootstrap row exists).

function ReviewApplyTab({
  run,
  runs,
  agents,
  prereqs,
  selectedRun,
  selectedRunCapabilities,
  canView,
}: {
  run:                     SupportImportJob | null
  runs:                    AgentBootstrapRunSummary[]
  agents:                  Array<{ id: number; name: string }>
  prereqs:                 BootstrapPrereq[]
  selectedRun:             AgentBootstrapRunDetailType | null
  selectedRunCapabilities: { apply: boolean; cancel: boolean; edit: boolean }
  canView:                 boolean
}) {
  // Polling cadence: faster while a run is in flight.
  useEffect(() => {
    const inFlight = runs.some((r) => isBuildInFlight(r.state)) ||
      (selectedRun && isBuildInFlight(selectedRun.state))
    const interval = inFlight ? 5000 : 30000
    const tick = setInterval(() => {
      router.reload({
        only:           ["bootstrap_runs", "selected_run", "existing_connections"],
      })
    }, interval)
    return () => clearInterval(tick)
  }, [runs, selectedRun])

  if (!canView) {
    return (
      <Card className="p-5 sm:p-6">
        <p className="text-sm text-app-muted">
          You don't have permission to view bootstrap runs.
        </p>
      </Card>
    )
  }

  // No active onboarding + no historical runs → empty state.
  if (!run && runs.length === 0) {
    return (
      <div className="space-y-6">
        <Card className="p-5 sm:p-6">
          <div className="flex flex-col items-start gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full border border-border bg-white/5 text-app-muted">
              <Sparkles size={20} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-app-text">Nothing to review yet</h3>
              <p className="mt-1 max-w-2xl text-sm text-app-muted">
                Once data ingestion finishes, head to the Configure agents tab to set up your
                groups, then click Build agents. Synthesized proposals will land here for review.
              </p>
            </div>
            <PrereqList prereqs={prereqs} />
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {run && (
        <Card className="p-4">
          <div className="mb-2 text-xs uppercase tracking-wide text-app-muted">Onboarding progress</div>
          <OnboardingRunPhaseStrip phases={run.phases} />
        </Card>
      )}

      <Card className="overflow-hidden p-0">
        <div className="grid grid-cols-1 gap-0 lg:grid-cols-[260px_1fr]">
          <ReviewSidebar
            run={run}
            historyRuns={runs}
            agents={agents}
            selectedRun={selectedRun}
          />
          <div className="border-t border-border lg:border-l lg:border-t-0">
            <ReviewMain
              run={run}
              selectedRun={selectedRun}
              selectedRunCapabilities={selectedRunCapabilities}
            />
          </div>
        </div>
      </Card>
    </div>
  )
}

// Sidebar — lists groups in the active SupportImportJob first, then any
// historical bootstrap runs not tied to a group (e.g. legacy data, or a
// previous SupportImportJob's runs). Selecting a row navigates to the run
// detail; the right pane swaps to that run's review surface.
function ReviewSidebar({
  run,
  historyRuns,
  agents,
  selectedRun,
}: {
  run:         SupportImportJob | null
  historyRuns: AgentBootstrapRunSummary[]
  agents:      Array<{ id: number; name: string }>
  selectedRun: AgentBootstrapRunDetailType | null
}) {
  const agentNameById = useMemo(
    () => Object.fromEntries(agents.map((a) => [a.id, a.name])),
    [agents],
  )
  const groupBootstrapIds = useMemo(() => {
    const s = new Set<number>()
    if (run) for (const g of run.proposed_agents) {
      s.add(g.id)
    }
    return s
  }, [run])
  const orphans = useMemo(
    () => historyRuns.filter((r) => !groupBootstrapIds.has(r.id)),
    [historyRuns, groupBootstrapIds],
  )

  const selectRun = (id: number) => {
    router.visit(`/settings/onboarding?tab=review&run_id=${id}`, { preserveState: false })
  }

  const selectGroup = (g: SupportImportProposedAgent) => {
    selectRun(g.id)
  }

  return (
    <aside className="bg-app-panel2/40 p-3">
      {run && run.proposed_agents.length > 0 && (
        <div className="mb-3">
          <div className="mb-1 px-1 text-[10px] uppercase tracking-wide text-app-muted">
            Agent groups
          </div>
          <ul className="space-y-1">
            {run.proposed_agents
              .slice()
              .sort((a, b) => a.position - b.position)
              .map((g) => {
                const isSelected =
                  selectedRun !== null && g.id === selectedRun.id
                return (
                  <li key={g.id}>
                    <button
                      type="button"
                      onClick={() => selectGroup(g)}
                      className={cn(
                        "w-full rounded-control px-2 py-2 text-left text-sm transition",
                        isSelected
                          ? "bg-brand-redSoft/40 text-app-text"
                          : "text-app-muted hover:bg-white/5 hover:text-app-text",
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-medium">{g.name}</span>
                        <Badge tone={BOOTSTRAP_STATE_TONE[g.build_state]}>
                          {g.build_state.replace(/_/g, " ")}
                        </Badge>
                      </div>
                      <div className="mt-0.5 truncate text-[11px] text-app-muted">
                        {g.connection_ids.length} connection{g.connection_ids.length === 1 ? "" : "s"}
                        {g.compare_against_agent_id && agentNameById[g.compare_against_agent_id]
                          ? ` · vs ${agentNameById[g.compare_against_agent_id]}`
                          : ""}
                      </div>
                    </button>
                  </li>
                )
              })}
          </ul>
        </div>
      )}
      {orphans.length > 0 && (
        <div>
          <div className="mb-1 px-1 text-[10px] uppercase tracking-wide text-app-muted">
            Other bootstrap runs
          </div>
          <ul className="space-y-1">
            {orphans.map((r) => {
              const isSelected = selectedRun?.id === r.id
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => selectRun(r.id)}
                    className={cn(
                      "w-full rounded-control px-2 py-2 text-left text-sm transition",
                      isSelected
                        ? "bg-brand-redSoft/40 text-app-text"
                        : "text-app-muted hover:bg-white/5 hover:text-app-text",
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">Run #{r.id}</span>
                      <Badge tone={BOOTSTRAP_STATE_TONE[r.state]}>{r.state.replace(/_/g, " ")}</Badge>
                    </div>
                    <div className="mt-0.5 text-[11px] text-app-muted">
                      {fmtCompactRelative(r.created_at)}
                      {r.agent_name ? ` · → ${r.agent_name}` : " · net-new"}
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
      <div className="mt-3 border-t border-border pt-2">
        <Link
          href="/support_import_proposed_agents"
          className="inline-block px-1 text-[11px] text-app-muted hover:text-app-text"
        >
          Open full bootstrap dashboard →
        </Link>
      </div>
    </aside>
  )
}

function ReviewMain({
  run,
  selectedRun,
  selectedRunCapabilities,
}: {
  run:                     SupportImportJob | null
  selectedRun:             AgentBootstrapRunDetailType | null
  selectedRunCapabilities: { apply: boolean; cancel: boolean; edit: boolean }
}) {
  // No selected run, but the URL may have ?group_id=… for an
  // unstarted group. Show the placeholder for that case.
  if (!selectedRun) {
    const groupId = (() => {
      if (typeof window === "undefined") return null
      const url = new URL(window.location.href)
      const raw = url.searchParams.get("group_id")
      const id = raw ? Number(raw) : NaN
      return Number.isFinite(id) ? id : null
    })()
    const group = run?.proposed_agents.find((g) => g.id === groupId) ?? null
    return (
      <div className="flex h-full min-h-[260px] flex-col items-start justify-center gap-2 px-6 py-10">
        <h3 className="text-sm font-semibold text-app-text">
          {group ? `${group.name}` : "Pick a group to review"}
        </h3>
        <p className="max-w-md text-sm text-app-muted">
          {group
            ? "Bootstrap not started yet for this group. Once Build agents has been clicked on the Configure tab, the AI proposals will appear here."
            : "Select an agent group from the sidebar to review the AI's proposals."}
        </p>
      </div>
    )
  }

  return (
    <div className="p-4">
      <SelectedRunPanel
        run={selectedRun}
        capabilities={selectedRunCapabilities}
        allRuns={[]}
      />
    </div>
  )
}

function PrereqList({ prereqs }: { prereqs: BootstrapPrereq[] }) {
  return (
    <div className="w-full max-w-2xl rounded-control border border-border bg-white/[0.02] p-4">
      <div className="mb-2 text-xs uppercase tracking-wide text-app-muted">Prerequisites</div>
      <ul className="space-y-1.5">
        {prereqs.map((p) => (
          <li key={p.key} className="flex items-center gap-2 text-sm">
            {p.met ? (
              <CheckCircle2 size={14} className="text-status-success" />
            ) : (
              <Circle size={14} className="text-app-muted" />
            )}
            <span className={p.met ? "text-app-text" : "text-app-muted"}>{p.label}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function AgentBootstrapRunDetail({
  run,
  capabilities,
}: {
  run: AgentBootstrapRunDetailType
  capabilities: { apply: boolean; cancel: boolean; edit: boolean }
  embedded?: boolean
}) {
  const [busy, setBusy] = useState<string | null>(null)
  const [editing, setEditing] = useState<SupportImportProposalSummary | null>(null)
  const [editedText, setEditedText] = useState("")
  const proposalsByKind = useMemo(() => {
    const groups = new Map<SupportImportProposalKind, SupportImportProposalSummary[]>()
    for (const proposal of run.proposals) {
      const list = groups.get(proposal.kind) ?? []
      list.push(proposal)
      groups.set(proposal.kind, list)
    }
    return Array.from(groups.entries()).sort((a, b) => proposalKindOrder(a[0]) - proposalKindOrder(b[0]))
  }, [run.proposals])

  const postProposal = (proposal: SupportImportProposalSummary, action: "approve" | "reject") => {
    setBusy(`${action}:${proposal.id}`)
    router.post(`/support_import_proposed_agents/${run.id}/proposals/${proposal.id}/${action}`, {}, {
      preserveScroll: true,
      onSuccess: () => toast.success(action === "approve" ? "Proposal approved" : "Proposal rejected"),
      onError: () => toast.error("Proposal update failed"),
      onFinish: () => setBusy(null),
    })
  }

  const openEdit = (proposal: SupportImportProposalSummary) => {
    setEditing(proposal)
    setEditedText(JSON.stringify(effectiveProposalPayload(proposal), null, 2))
  }

  const saveEdit = () => {
    if (!editing) return
    let parsed: Record<string, unknown>
    try {
      parsed = JSON.parse(editedText)
    } catch {
      toast.error("Edited payload is not valid JSON")
      return
    }
    setBusy(`edit:${editing.id}`)
    const body = {
      proposal: { edited_payload: parsed },
    } as any
    router.patch(`/support_import_proposed_agents/${run.id}/proposals/${editing.id}`, body, {
      preserveScroll: true,
      onSuccess: () => { toast.success("Proposal edited"); setEditing(null) },
      onError: () => toast.error("Could not save edit"),
      onFinish: () => setBusy(null),
    })
  }

  const applyApproved = () => {
    setBusy("apply")
    router.post(`/support_import_proposed_agents/${run.id}/apply`, {}, {
      preserveScroll: true,
      onSuccess: () => toast.success("Approved onboarding changes applied"),
      onError: () => toast.error("Apply failed"),
      onFinish: () => setBusy(null),
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-control border border-border bg-white/[0.02] px-4 py-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-app-text">
              {run.agent_name || (run.proposed_agent_attrs?.["name"] as string | undefined) || `Run #${run.id}`}
            </h3>
            <Badge tone={BOOTSTRAP_STATE_TONE[run.state]}>{run.state.replace(/_/g, " ")}</Badge>
          </div>
          <div className="mt-1 text-sm text-app-muted">
            {run.compare_against_agent_name ? `Augments ${run.compare_against_agent_name}` : "Net-new agent"}
            {run.created_at ? ` · created ${fmtCompactRelative(run.created_at)}` : ""}
          </div>
        </div>
        {capabilities.apply && run.state === "ready_for_review" && (
          <Button onClick={applyApproved} disabled={busy === "apply"}>
            <CheckCircle2 size={14} className="mr-1.5" />
            {busy === "apply" ? "Applying…" : "Apply approved changes"}
          </Button>
        )}
      </div>

      {Object.keys(run.proposed_agent_attrs ?? {}).length > 0 && (
        <ProposalPayloadPreview title="Agent settings" payload={run.proposed_agent_attrs ?? {}} />
      )}

      {proposalsByKind.length === 0 ? (
        <Card className="p-5">
          <p className="text-sm text-app-muted">
            {isBuildInFlight(run.state)
              ? "Forge is still generating recommendations for this group."
              : "No proposals were generated for this group."}
          </p>
        </Card>
      ) : (
        proposalsByKind.map(([kind, proposals]) => (
          <section key={kind} className="space-y-2">
            <div className="text-xs uppercase tracking-wide text-app-muted">{proposalKindLabel(kind)}</div>
            {proposals.map((proposal) => (
              <div key={proposal.id} className="rounded-control border border-border bg-white/[0.02] px-4 py-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="truncate text-sm font-semibold text-app-text">{proposalTitle(proposal)}</h4>
                      <Badge tone={proposalStatusTone(proposal.status)}>{proposal.status}</Badge>
                    </div>
                    <div className="mt-0.5 text-[12px] text-app-muted">
                      {proposal.proposed_key ? `${proposal.proposed_key} · ` : ""}
                      {proposal.duplicate_of_label ? `possible duplicate: ${proposal.duplicate_of_label}` : proposal.provenance}
                    </div>
                  </div>
                  {capabilities.edit && proposal.status !== "applied" && (
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(proposal)}>Edit</Button>
                      <Button variant="ghost" size="sm" onClick={() => postProposal(proposal, "reject")} disabled={busy === `reject:${proposal.id}`}>
                        Reject
                      </Button>
                      <Button size="sm" onClick={() => postProposal(proposal, "approve")} disabled={busy === `approve:${proposal.id}`}>
                        Approve
                      </Button>
                    </div>
                  )}
                </div>
                <ProposalPayloadPreview payload={effectiveProposalPayload(proposal)} />
              </div>
            ))}
          </section>
        ))
      )}

      <Dialog open={editing != null} onClose={() => setEditing(null)} size="lg">
        <DialogHeader title={editing ? `Edit ${proposalTitle(editing)}` : "Edit proposal"} />
        <DialogBody>
          <textarea
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            className="h-80 w-full rounded-control border border-border bg-black/30 p-3 font-mono text-xs text-app-text outline-none focus:border-brand-red/60"
            spellCheck={false}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
          <Button onClick={saveEdit} disabled={busy?.startsWith("edit:")}>Save edit</Button>
        </DialogFooter>
      </Dialog>
    </div>
  )
}

function ProposalPayloadPreview({ title, payload }: { title?: string; payload: Record<string, unknown> }) {
  const entries = Object.entries(payload).filter(([, value]) => value != null && value !== "")
  return (
    <div className={cn("mt-3 rounded-control border border-border/70 bg-black/20 px-3 py-2", title && "mt-0")}>
      {title && <div className="mb-2 text-xs uppercase tracking-wide text-app-muted">{title}</div>}
      {entries.length === 0 ? (
        <p className="text-xs text-app-muted">No structured payload.</p>
      ) : (
        <dl className="grid gap-x-4 gap-y-2 text-xs sm:grid-cols-2">
          {entries.slice(0, 10).map(([key, value]) => (
            <div key={key} className="min-w-0">
              <dt className="text-app-muted">{key}</dt>
              <dd className="truncate text-app-text">{formatPayloadValue(value)}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}

function effectiveProposalPayload(proposal: SupportImportProposalSummary): Record<string, unknown> {
  return { ...(proposal.payload ?? {}), ...(proposal.edited_payload ?? {}) }
}

function proposalKindOrder(kind: SupportImportProposalKind): number {
  return {
    agent_pool: 0,
    ticket_queue: 1,
    pool_membership: 2,
    team_invite: 3,
    agent_header: 4,
    escalation_reason: 5,
    topic: 6,
    faq: 7,
    skill: 8,
    escalation_policy: 9,
  }[kind] ?? 99
}

function proposalKindLabel(kind: SupportImportProposalKind): string {
  return kind.replace(/_/g, " ")
}

function proposalTitle(proposal: SupportImportProposalSummary): string {
  return proposal.proposed_name || proposal.proposed_key || `${proposalKindLabel(proposal.kind)} #${proposal.id}`
}

function proposalStatusTone(status: string): BadgeTone {
  if (status === "approved" || status === "applied") return "success"
  if (status === "rejected") return "danger"
  return "neutral"
}

function formatPayloadValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(", ")
  if (typeof value === "object" && value !== null) return JSON.stringify(value)
  return String(value)
}

// State-router for the embedded run panel — picks State B / C / D / E
// based on the selected run's lifecycle. C is the heavy one and embeds
// the full review UI inline via <AgentBootstrapRunDetail embedded />.
function SelectedRunPanel({
  run,
  capabilities,
  allRuns: _allRuns,
}: {
  run: AgentBootstrapRunDetailType
  capabilities: { apply: boolean; cancel: boolean; edit: boolean }
  allRuns: AgentBootstrapRunSummary[]
}) {
  // State D — applied
  if (run.state === "applied") {
    const counts = countByKind(run)
    return (
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-control bg-status-success/15 text-status-success">
              <CheckCircle2 size={16} />
            </div>
            <div>
              <div className="text-sm font-semibold text-app-text">
                Run #{run.id} applied
                {run.applied_at ? ` ${fmtCompactRelative(run.applied_at)}` : ""}
              </div>
              <div className="mt-0.5 text-[11px] text-app-muted">
                {summarizeCounts(counts)}
                {run.agent_name ? ` → applied to ${run.agent_name}` : ""}
              </div>
            </div>
          </div>
          {run.agent_id && (
            <Button onClick={() => router.visit(`/agents/${run.agent_id}`)}>
              View agent <ChevronRight size={14} className="ml-1" />
            </Button>
          )}
        </div>
      </Card>
    )
  }

  // State E — terminal but not applied
  if (run.state === "cancelled" || run.state === "failed") {
    return (
      <Card className="p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-control bg-white/5 text-app-muted">
            <AlertTriangle size={16} />
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-app-text">
              Run #{run.id} · {run.state}
              <Badge tone={BOOTSTRAP_STATE_TONE[run.state]}>{run.state}</Badge>
            </div>
            <div className="mt-0.5 text-[11px] text-app-muted">
              Created {fmtCompactRelative(run.created_at)} by {run.created_by_user_email}
            </div>
          </div>
        </div>
      </Card>
    )
  }

  // States B + C — pending / generating / ready_for_review.
  // The shared RunDetail component handles polling, kind tabs, buckets,
  // proposal cards, edit dialog, cluster drawer, and the Apply confirmation.
  return (
    <AgentBootstrapRunDetail
      run={run}
      capabilities={capabilities}
      embedded
    />
  )
}

function countByKind(run: AgentBootstrapRunDetailType) {
  const c: Record<string, number> = {
    agent_header: 0,
    skill: 0,
    faq: 0,
    escalation_reason: 0,
    topic: 0,
    agent_pool: 0,
    ticket_queue: 0,
    escalation_policy: 0,
    pool_membership: 0,
    team_invite: 0,
  }
  for (const p of run.proposals) {
    if (p.status === "applied" || p.status === "approved") c[p.kind] += 1
  }
  return c
}

function isBuildInFlight(state: AgentBootstrapRunState): boolean {
  return state === "pending" || state === "clustering" || state === "summarizing" || state === "synthesizing"
}

function summarizeCounts(c: Record<string, number>): string {
  const parts: string[] = []
  if (c.skill) parts.push(`${c.skill} skill${c.skill === 1 ? "" : "s"}`)
  if (c.faq) parts.push(`${c.faq} FAQ${c.faq === 1 ? "" : "s"}`)
  if (c.escalation_reason) parts.push(`${c.escalation_reason} escalation reason${c.escalation_reason === 1 ? "" : "s"}`)
  if (c.topic) parts.push(`${c.topic} topic${c.topic === 1 ? "" : "s"}`)
  if (c.agent_header) parts.push(`agent header`)
  if (c.agent_pool) parts.push(`${c.agent_pool} pool${c.agent_pool === 1 ? "" : "s"}`)
  if (c.ticket_queue) parts.push(`${c.ticket_queue} queue${c.ticket_queue === 1 ? "" : "s"}`)
  if (c.escalation_policy) parts.push(c.escalation_policy === 1 ? "1 policy" : `${c.escalation_policy} policies`)
  return parts.length ? parts.join(", ") : "no changes"
}

// ---------- lifecycle controls ----------

function RestoreOnboardingButton() {
  const [busy, setBusy] = useState(false)
  const submit = () => {
    setBusy(true)
    router.post("/companies/lifecycle/restore_onboarding", {}, {
      onSuccess: () => toast.success("Onboarding tab restored"),
      onError: () => toast.error("Could not restore onboarding"),
      onFinish: () => setBusy(false),
    })
  }
  return (
    <Button variant="ghost" size="sm" onClick={submit} disabled={busy}>
      {busy ? "Restoring…" : "Restore to onboarding"}
    </Button>
  )
}

// ---------- shared ----------

function FormField({
  label,
  required,
  hint,
  children,
}: {
  label: React.ReactNode
  required?: boolean
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <div className="mb-1 flex items-center gap-1 text-xs uppercase tracking-wide text-app-muted">
        {label}
        {required && <span className="text-brand-red">*</span>}
      </div>
      {hint && <div className="mb-1 text-[11px] text-app-muted">{hint}</div>}
      {children}
    </label>
  )
}
